import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, fmt } from "../../../services/erpApi";
import { PageHeader, Modal, LoadingScreen, EmptyState } from "../../../components/admin/EssUi";
import { useToast } from "../../../components/admin/ToastContext";
import { Plus, Search, Pencil, Trash2, X, ChevronUp, ChevronDown, Users, Check, RefreshCw, Send, MessageCircle } from "lucide-react";

const inputStyle = {
  width: "100%", padding: "9px 12px", border: "1px solid #cbd5e1", borderRadius: "6px",
  fontSize: "13.5px", outline: "none", background: "#fff", boxSizing: "border-box",
};
const labelStyle = { fontSize: "13px", fontWeight: "600", color: "#334155", marginBottom: "6px", display: "block" };
const iconBtn = (color) => ({
  border: "1px solid #cbd5e1", background: "white", color, cursor: "pointer",
  padding: "5px 8px", borderRadius: 6, display: "flex", alignItems: "center",
});

const norm = (r) => (r && typeof r === "object" && !Array.isArray(r) && "data" in r ? r.data : r);

const FormField = ({ label, children, required }) => (
  <div style={{ marginBottom: "14px" }}>
    <label style={labelStyle}>{label}{required && <span style={{ color: "#be123c" }}> *</span>}</label>
    {children}
  </div>
);

const activeBadge = (v) =>
  v === false
    ? { text: "#be123c", bg: "#fff1f2", label: "Inactive" }
    : { text: "#225f2b", bg: "#f0f7f0", label: "Active" };

const renderField = (fld, value, onChange) => {
  if (fld.type === "select") {
    return (
      <select value={value ?? ""} onChange={(e) => onChange(e.target.value)} style={inputStyle}>
        <option value="">None…</option>
        {(fld.options || []).map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    );
  }
  if (fld.type === "checkbox") {
    return (
      <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} style={{ width: 18, height: 18, cursor: "pointer" }} />
    );
  }
  if (fld.type === "color") {
    return (
      <input type="color" value={value || "#225f2b"} onChange={(e) => onChange(e.target.value)} style={{ width: 56, height: 38, cursor: "pointer", border: "1px solid #cbd5e1", borderRadius: 6, padding: 2 }} />
    );
  }
  if (fld.type === "number") {
    return (
      <input type="number" min={fld.min ?? 0} max={fld.max} value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder={fld.placeholder || ""} style={inputStyle} />
    );
  }
  if (fld.type === "textarea") {
    return <textarea value={value ?? ""} onChange={(e) => onChange(e.target.value)} rows={fld.rows || 3} placeholder={fld.placeholder || ""} style={{ ...inputStyle, resize: "vertical" }} />;
  }
  return <input value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder={fld.placeholder || ""} style={inputStyle} />;
};

const CrudPanel = ({ rows, fields, loading, onCreate, onUpdate, onDelete, onToggleActive, searchKey = "name", emptyTitle, hint }) => {
  const toast = useToast();
  const [search, setSearch] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const nameField = fields.find((f) => f.name === "name");
  const listFields = fields.filter((f) => f.name !== "is_active");

  const openNew = () => {
    setEditing(null);
    const f = {};
    fields.forEach((fld) => {
      f[fld.name] = fld.type === "checkbox" ? (fld.default ?? true) : (fld.default ?? "");
    });
    setForm(f);
    setShowModal(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    const f = {};
    fields.forEach((fld) => {
      f[fld.name] = row[fld.name] ?? (fld.type === "checkbox" ? true : "");
    });
    setForm(f);
    setShowModal(true);
  };

  const submit = async () => {
    const payload = {};
    fields.forEach((fld) => {
      const v = form[fld.name];
      if (fld.type === "number") payload[fld.name] = v !== "" && v != null ? parseFloat(v) : undefined;
      else if (fld.type === "checkbox") payload[fld.name] = !!v;
      else if (fld.type === "select") payload[fld.name] = v ? parseInt(v) : undefined;
      else payload[fld.name] = (v ?? "").trim() || undefined;
    });
    if (nameField && !payload.name) { toast(`${nameField.label} is required`, "error"); return; }
    setSaving(true);
    try {
      if (editing) { await onUpdate(editing.id, payload); toast("Updated", "success"); }
      else { await onCreate(payload); toast("Created", "success"); }
      setShowModal(false);
      setEditing(null);
    } catch (e) {
      toast(e.message || "Save failed", "error");
    }
    setSaving(false);
  };

  const filtered = useMemo(() => {
    if (!search.trim()) return rows;
    const q = search.toLowerCase();
    return rows.filter((r) => String(r[searchKey] || "").toLowerCase().includes(q));
  }, [rows, search, searchKey]);

  if (loading) return <LoadingScreen />;

  return (
    <div style={{ background: "white", borderRadius: 8, border: "1px solid #e2e7e2", overflow: "hidden" }}>
      <div style={{ display: "flex", gap: 12, alignItems: "center", padding: 16, borderBottom: "1px solid #e2e7e2", flexWrap: "wrap" }}>
        {hint && <div style={{ fontSize: 13, color: "#64748b" }}>{hint}</div>}
        <div style={{ position: "relative", flex: 1, minWidth: 220 }}>
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "#94a3b8" }} />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search…" style={{ ...inputStyle, padding: "9px 12px 9px 36px" }} />
        </div>
        <button onClick={openNew} className="ess-btn ess-btn-primary"><Plus size={16} /> New</button>
      </div>
      {filtered.length === 0 ? (
        <EmptyState  title={emptyTitle || "Nothing here"} subtitle="Create a new entry" />
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "#fafbfa", borderBottom: "1px solid #e2e7e2" }}>
                {listFields.map((f) => <th key={f.name} className="ess-th" style={{ minWidth: f.type === "checkbox" ? 90 : 130, textAlign: f.type === "number" ? "right" : "left" }}>{f.label}</th>)}
                <th className="ess-th" style={{ minWidth: 90 }}>Status</th>
                <th className="ess-th ess-th-r" style={{ minWidth: 150 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((row, i) => {
                const st = activeBadge(row.is_active);
                return (
                  <tr key={row.id} className="ess-tr" style={{ borderBottom: i < filtered.length - 1 ? "1px solid #f1f3f0" : "none" }}>
                    {listFields.map((f) => (
                      <td key={f.name} className="ess-td" style={{ fontWeight: f.name === "name" ? 600 : 400, color: f.name === "name" ? "#225f2b" : "#475569", textAlign: f.type === "number" ? "right" : "left", whiteSpace: "nowrap" }}>
                        {f.type === "color" ? (
                          <span title={row[f.name] || ""} style={{ display: "inline-block", width: 18, height: 18, borderRadius: 4, background: row[f.name] || "#e2e7e2", border: "1px solid #cbd5e1", verticalAlign: "middle" }} />
                        ) : f.type === "checkbox" ? (row[f.name] ? "Yes" : "No") : f.render ? f.render(row[f.name], row) : (row[f.name] ?? "—")}
                      </td>
                    ))}
                    <td className="ess-td">
                      <span style={{ background: st.bg, color: st.text, padding: "3px 9px", borderRadius: 6, fontSize: 11, fontWeight: 600 }}>{st.label}</span>
                    </td>
                    <td className="ess-td" style={{ whiteSpace: "nowrap", textAlign: "right" }}>
                      <div style={{ display: "flex", gap: 4, justifyContent: "flex-end" }}>
                        <button title="Toggle active" onClick={() => onToggleActive(row)} style={iconBtn("#0f766e")}><Check size={13} /></button>
                        <button title="Edit" onClick={() => openEdit(row)} style={iconBtn("#225f2b")}><Pencil size={13} /></button>
                        <button title="Delete" onClick={() => { if (window.confirm(`Delete "${row.name}"?`)) onDelete(row); }} style={iconBtn("#be123c")}><Trash2 size={13} /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <Modal open={showModal} onClose={() => setShowModal(false)} title={editing ? `Edit ${editing.name || editing.id}` : "New entry"} size="md">
        <div>
          {fields.map((f) => (
            <FormField key={f.name} label={f.label} required={f.required}>
              {renderField(f, form[f.name], (v) => setForm((prev) => ({ ...prev, [f.name]: v })))}
            </FormField>
          ))}
          <div style={{ display: "flex", gap: 12, justifyContent: "flex-end", borderTop: "1px solid #e2e7e2", paddingTop: 20 }}>
            <button onClick={() => setShowModal(false)} className="ess-btn ess-btn-secondary">Cancel</button>
            <button disabled={saving} onClick={submit} className="ess-btn ess-btn-primary">{saving ? "Saving…" : "Save"}</button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

const StagePanel = ({ rows, teams, loading, onCreate, onUpdate, onDelete, onToggleActive, onReorder }) => {
  const toast = useToast();
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const fields = [
    { name: "name", label: "Stage name", type: "text", required: true },
    { name: "sequence", label: "Sequence", type: "number" },
    { name: "probability", label: "Default probability (%)", type: "number", min: 0, max: 100 },
    { name: "team_id", label: "Team", type: "select", options: teams.map((t) => ({ value: t.id, label: t.name })) },
    { name: "fold", label: "Folded (collapsed in kanban)", type: "checkbox", default: false },
    { name: "is_won", label: "Terminal — Won", type: "checkbox", default: false },
    { name: "is_lost", label: "Terminal — Lost", type: "checkbox", default: false },
    { name: "is_active", label: "Active", type: "checkbox", default: true },
    { name: "auto_create_quotation", label: "Auto-create quotation", type: "checkbox", default: false },
  ];

  const openNew = () => {
    setEditing(null);
    setForm({ name: "", sequence: rows.length + 1, probability: "", team_id: "", fold: false, is_won: false, is_lost: false, is_active: true, auto_create_quotation: false });
    setShowModal(true);
  };
  const openEdit = (row) => {
    setEditing(row);
    setForm({ name: row.name || "", sequence: row.sequence ?? "", probability: row.probability ?? "", team_id: row.team_id ?? "", fold: !!row.fold, is_won: !!row.is_won, is_lost: !!row.is_lost, is_active: row.is_active !== false, auto_create_quotation: !!row.auto_create_quotation });
    setShowModal(true);
  };
  const submit = async () => {
    if (!form.name.trim()) { toast("Stage name is required", "error"); return; }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        sequence: form.sequence !== "" ? parseInt(form.sequence) : undefined,
        probability: form.probability !== "" ? parseFloat(form.probability) : undefined,
        team_id: form.team_id ? parseInt(form.team_id) : undefined,
        fold: !!form.fold,
        is_won: !!form.is_won,
        is_lost: !!form.is_lost,
        is_active: !!form.is_active,
        auto_create_quotation: !!form.auto_create_quotation,
      };
      if (editing) { await onUpdate(editing.id, payload); toast("Updated", "success"); }
      else { await onCreate(payload); toast("Created", "success"); }
      setShowModal(false);
      setEditing(null);
    } catch (e) { toast(e.message || "Save failed", "error"); }
    setSaving(false);
  };

  if (loading) return <LoadingScreen />;

  return (
    <div style={{ background: "white", borderRadius: 8, border: "1px solid #e2e7e2", overflow: "hidden" }}>
      <div style={{ display: "flex", gap: 12, alignItems: "center", padding: 16, borderBottom: "1px solid #e2e7e2" }}>
        <div style={{ fontSize: 13, color: "#64748b" }}>
          Order the stages with ↑/↓ — this drives the Kanban columns sequence.
        </div>
        <div style={{ marginLeft: "auto" }}>
          <button onClick={openNew} className="ess-btn ess-btn-primary"><Plus size={16} /> New stage</button>
        </div>
      </div>
      {rows.length === 0 ? (
        <EmptyState  title="No stages" subtitle="Create your first pipeline stage" />
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "#fafbfa", borderBottom: "1px solid #e2e7e2" }}>
                <th className="ess-th" style={{ minWidth: 90 }}>Order</th>
                <th className="ess-th" style={{ minWidth: 170 }}>Name</th>
                <th className="ess-th" style={{ minWidth: 80, textAlign: "right" }}>Seq</th>
                <th className="ess-th" style={{ minWidth: 130, textAlign: "right" }}>Prob</th>
                <th className="ess-th" style={{ minWidth: 120 }}>Team</th>
                <th className="ess-th" style={{ minWidth: 90 }}>Folded</th>
                <th className="ess-th" style={{ minWidth: 120 }}>Type</th>
                <th className="ess-th" style={{ minWidth: 90 }}>Status</th>
                <th className="ess-th ess-th-r" style={{ minWidth: 200 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => {
                const st = activeBadge(row.is_active);
                const term = row.is_won || row.is_lost;
                return (
                  <tr key={row.id} className="ess-tr" style={{ borderBottom: i < rows.length - 1 ? "1px solid #f1f3f0" : "none" }}>
                    <td className="ess-td" style={{ whiteSpace: "nowrap" }}>
                      <div style={{ display: "flex", gap: 2 }}>
                        <button title="Move up" disabled={i === 0} onClick={() => onReorder(row, -1)} style={{ ...iconBtn("#475569"), padding: "3px 6px", opacity: i === 0 ? 0.4 : 1 }}><ChevronUp size={13} /></button>
                        <button title="Move down" disabled={i === rows.length - 1} onClick={() => onReorder(row, 1)} style={{ ...iconBtn("#475569"), padding: "3px 6px", opacity: i === rows.length - 1 ? 0.4 : 1 }}><ChevronDown size={13} /></button>
                      </div>
                    </td>
                    <td className="ess-td" style={{ fontWeight: 600, color: "#225f2b", whiteSpace: "nowrap" }}>{row.name}</td>
                    <td className="ess-td" style={{ textAlign: "right", color: "#475569" }}>{row.sequence ?? "—"}</td>
                    <td className="ess-td" style={{ textAlign: "right", color: "#475569" }}>{row.probability != null ? `${row.probability}%` : "—"}</td>
                    <td className="ess-td" style={{ color: "#64748b", whiteSpace: "nowrap" }}>{teams.find((t) => t.id === row.team_id)?.name || "—"}</td>
                    <td className="ess-td" style={{ color: "#475569" }}>{row.fold ? "Yes" : "No"}</td>
                    <td className="ess-td" style={{ whiteSpace: "nowrap" }}>
                      {term ? (
                        <span style={{ padding: "3px 9px", borderRadius: 6, fontSize: 11, fontWeight: 700, background: row.is_won ? "#f0f7f0" : "#fff1f2", color: row.is_won ? "#225f2b" : "#be123c" }}>{row.is_won ? "WON" : "LOST"}</span>
                      ) : (
                        <span style={{ fontSize: 12, color: "#94a3b8" }}>Open</span>
                      )}
                    </td>
                    <td className="ess-td"><span style={{ background: st.bg, color: st.text, padding: "3px 9px", borderRadius: 6, fontSize: 11, fontWeight: 600 }}>{st.label}</span></td>
                    <td className="ess-td" style={{ whiteSpace: "nowrap", textAlign: "right" }}>
                      <div style={{ display: "flex", gap: 4, justifyContent: "flex-end" }}>
                        <button title="Toggle active" onClick={() => onToggleActive(row)} style={iconBtn("#0f766e")}><Check size={13} /></button>
                        <button title="Edit" onClick={() => openEdit(row)} style={iconBtn("#225f2b")}><Pencil size={13} /></button>
                        <button title="Delete" onClick={() => { if (window.confirm(`Delete stage "${row.name}"? (blocked if in use)`)) onDelete(row); }} style={iconBtn("#be123c")}><Trash2 size={13} /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <Modal open={showModal} onClose={() => setShowModal(false)} title={editing ? `Edit stage — ${editing.name}` : "New stage"} size="md">
        <div>
          <div style={{ display: "grid", gap: "12px 16px" }} className="ess-rg-2">
            <FormField label="Stage name" required>
              <input value={form.name} onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))} placeholder="e.g. Qualified" style={inputStyle} />
            </FormField>
            <FormField label="Sequence">
              <input type="number" value={form.sequence} onChange={(e) => setForm((prev) => ({ ...prev, sequence: e.target.value }))} placeholder="Order in pipeline" style={inputStyle} />
            </FormField>
            <FormField label="Default probability (%)">
              <input type="number" min="0" max="100" value={form.probability} onChange={(e) => setForm((prev) => ({ ...prev, probability: e.target.value }))} placeholder="0–100" style={inputStyle} />
            </FormField>
            <FormField label="Team">
              <select value={form.team_id} onChange={(e) => setForm((prev) => ({ ...prev, team_id: e.target.value }))} style={inputStyle}>
                <option value="">None…</option>
                {teams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </FormField>
            <FormField label="Folded in kanban">
              <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                <input type="checkbox" checked={!!form.fold} onChange={(e) => setForm((prev) => ({ ...prev, fold: e.target.checked }))} style={{ width: 16, height: 16 }} />
                <span style={{ fontSize: 12.5, color: "#475569" }}>Collapse this column by default</span>
              </label>
            </FormField>
            <FormField label="Active">
              <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                <input type="checkbox" checked={!!form.is_active} onChange={(e) => setForm((prev) => ({ ...prev, is_active: e.target.checked }))} style={{ width: 16, height: 16 }} />
                <span style={{ fontSize: 12.5, color: "#475569" }}>Visible in pipeline</span>
              </label>
            </FormField>
            <FormField label="Automation">
              <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                <input type="checkbox" checked={!!form.auto_create_quotation} onChange={(e) => setForm((prev) => ({ ...prev, auto_create_quotation: e.target.checked }))} style={{ width: 16, height: 16 }} />
                <span style={{ fontSize: 12.5, color: "#475569" }}>Auto-create quotation when lead enters this stage</span>
              </label>
            </FormField>
          </div>
          <div style={{ marginTop: 16, padding: "10px 12px", background: "#fafbfa", border: "1px solid #e2e7e2", borderRadius: 8 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 8 }}>Terminal stage</div>
            <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
              <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                <input type="checkbox" checked={!!form.is_won} onChange={(e) => setForm((prev) => ({ ...prev, is_won: e.target.checked, is_lost: e.target.checked ? false : prev.is_lost }))} style={{ width: 16, height: 16 }} />
                <span style={{ fontSize: 12.5, fontWeight: 600, color: "#225f2b" }}>Won</span>
              </label>
              <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                <input type="checkbox" checked={!!form.is_lost} onChange={(e) => setForm((prev) => ({ ...prev, is_lost: e.target.checked, is_won: e.target.checked ? false : prev.is_won }))} style={{ width: 16, height: 16 }} />
                <span style={{ fontSize: 12.5, fontWeight: 600, color: "#be123c" }}>Lost</span>
              </label>
            </div>
            <p style={{ fontSize: 11, color: "#94a3b8", marginTop: 6, marginBottom: 0 }}>A stage can only be Won <b>or</b> Lost, not both.</p>
          </div>
          <div style={{ display: "flex", gap: 12, justifyContent: "flex-end", borderTop: "1px solid #e2e7e2", paddingTop: 16, marginTop: 16 }}>
            <button onClick={() => setShowModal(false)} className="ess-btn ess-btn-secondary">Cancel</button>
            <button disabled={saving} onClick={submit} className="ess-btn ess-btn-primary">{saving ? "Saving…" : "Save"}</button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

const TeamPanel = ({ rows, users, loading, onCreate, onUpdate, onDelete, onToggleActive }) => {
  const toast = useToast();
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const [detail, setDetail] = useState(null);
  const [members, setMembers] = useState([]);
  const [newMember, setNewMember] = useState("");
  const [memberBusy, setMemberBusy] = useState(false);

  const fields = [
    { name: "name", label: "Team name", type: "text", required: true },
    { name: "description", label: "Description", type: "textarea" },
    { name: "target_revenue", label: "Target revenue (FCFA)", type: "number" },
    { name: "user_id", label: "Manager", type: "select", options: users.map((u) => ({ value: u.id, label: u.name || u.username || u.email || u.login })) },
    { name: "is_active", label: "Active", type: "checkbox", default: true },
  ];

  const openNew = () => {
    setEditing(null);
    setForm({ name: "", description: "", target_revenue: "", user_id: "", is_active: true });
    setShowModal(true);
  };
  const openEdit = (row) => {
    setEditing(row);
    setForm({ name: row.name || "", description: row.description || "", target_revenue: row.target_revenue ?? "", user_id: row.user_id ?? "", is_active: row.is_active !== false });
    setShowModal(true);
  };
  const submit = async () => {
    if (!form.name.trim()) { toast("Team name is required", "error"); return; }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        description: form.description.trim() || undefined,
        target_revenue: form.target_revenue !== "" ? parseFloat(form.target_revenue) : undefined,
        user_id: form.user_id ? parseInt(form.user_id) : undefined,
        is_active: !!form.is_active,
      };
      if (editing) { await onUpdate(editing.id, payload); toast("Updated", "success"); }
      else { await onCreate(payload); toast("Created", "success"); }
      setShowModal(false);
      setEditing(null);
    } catch (e) { toast(e.message || "Save failed", "error"); }
    setSaving(false);
  };

  const openMembers = async (team) => {
    setDetail(team);
    setNewMember("");
    setMembers([]);
    try {
      const d = await api.crm.teams.get(team.id);
      setMembers(d?.members || []);
    } catch { setMembers([]); }
  };

  const addMember = async () => {
    if (!detail || !newMember) return;
    const user = users.find((u) => String(u.id) === String(newMember));
    if (!user?.lewoo_uid) { toast("User has no lewoo_uid — refresh users", "error"); return; }
    setMemberBusy(true);
    try {
      await api.crm.teams.addMember(detail.id, { user_id: user.lewoo_uid });
      toast("Member added", "success");
      setNewMember("");
      const d = await api.crm.teams.get(detail.id);
      setMembers(d?.members || []);
    } catch (e) { toast(e.message || "Add member failed", "error"); }
    setMemberBusy(false);
  };

  const removeMember = async (m) => {
    if (!detail) return;
    if (!window.confirm(`Remove ${m.employee_name || m.login || "member"}?`)) return;
    try {
      await api.crm.teams.removeMember(detail.id, m.user_id);
      toast("Member removed", "success");
      const d = await api.crm.teams.get(detail.id);
      setMembers(d?.members || []);
    } catch (e) { toast(e.message || "Remove failed", "error"); }
  };

  if (loading) return <LoadingScreen />;

  return (
    <div style={{ background: "white", borderRadius: 8, border: "1px solid #e2e7e2", overflow: "hidden" }}>
      <div style={{ display: "flex", gap: 12, alignItems: "center", padding: 16, borderBottom: "1px solid #e2e7e2" }}>
        <div style={{ fontSize: 13, color: "#64748b" }}>Teams define the sales groups and their members.</div>
        <div style={{ marginLeft: "auto" }}>
          <button onClick={openNew} className="ess-btn ess-btn-primary"><Plus size={16} /> New team</button>
        </div>
      </div>
      {rows.length === 0 ? (
        <EmptyState  title="No teams" subtitle="Create your first sales team" />
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "#fafbfa", borderBottom: "1px solid #e2e7e2" }}>
                <th className="ess-th" style={{ minWidth: 160 }}>Name</th>
                <th className="ess-th" style={{ minWidth: 200 }}>Description</th>
                <th className="ess-th" style={{ minWidth: 130, textAlign: "right" }}>Target</th>
                <th className="ess-th" style={{ minWidth: 130 }}>Manager</th>
                <th className="ess-th" style={{ minWidth: 80 }}>Status</th>
                <th className="ess-th ess-th-r" style={{ minWidth: 210 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => {
                const st = activeBadge(row.is_active);
                return (
                  <tr key={row.id} className="ess-tr" style={{ borderBottom: i < rows.length - 1 ? "1px solid #f1f3f0" : "none" }}>
                    <td className="ess-td" style={{ fontWeight: 600, color: "#225f2b", whiteSpace: "nowrap" }}>{row.name}</td>
                    <td className="ess-td" style={{ color: "#64748b" }}>{row.description || "—"}</td>
                    <td className="ess-td" style={{ textAlign: "right", color: "#475569", whiteSpace: "nowrap" }}>{row.target_revenue ? fmt.money(row.target_revenue) : "—"}</td>
                    <td className="ess-td" style={{ color: "#64748b", whiteSpace: "nowrap" }}>{users.find((u) => String(u.id) === String(row.user_id))?.name || "—"}</td>
                    <td className="ess-td"><span style={{ background: st.bg, color: st.text, padding: "3px 9px", borderRadius: 6, fontSize: 11, fontWeight: 600 }}>{st.label}</span></td>
                    <td className="ess-td" style={{ whiteSpace: "nowrap", textAlign: "right" }}>
                      <div style={{ display: "flex", gap: 4, justifyContent: "flex-end" }}>
                        <button title="Members" onClick={() => openMembers(row)} style={iconBtn("#0f766e")}><Users size={13} /></button>
                        <button title="Toggle active" onClick={() => onToggleActive(row)} style={iconBtn("#475569")}><Check size={13} /></button>
                        <button title="Edit" onClick={() => openEdit(row)} style={iconBtn("#225f2b")}><Pencil size={13} /></button>
                        <button title="Delete" onClick={() => { if (window.confirm(`Delete team "${row.name}"?`)) onDelete(row); }} style={iconBtn("#be123c")}><Trash2 size={13} /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <Modal open={showModal} onClose={() => setShowModal(false)} title={editing ? `Edit team — ${editing.name}` : "New team"} size="md">
        <div>
          <div style={{ display: "grid", gap: "12px 16px" }} className="ess-rg-2">
            <FormField label="Team name" required>
              <input value={form.name} onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))} placeholder="e.g. North Region" style={inputStyle} />
            </FormField>
            <FormField label="Target revenue (FCFA)">
              <input type="number" min="0" value={form.target_revenue} onChange={(e) => setForm((prev) => ({ ...prev, target_revenue: e.target.value }))} placeholder="0" style={inputStyle} />
            </FormField>
            <FormField label="Manager">
              <select value={form.user_id} onChange={(e) => setForm((prev) => ({ ...prev, user_id: e.target.value }))} style={inputStyle}>
                <option value="">None…</option>
                {users.map((u) => <option key={u.id} value={u.id}>{u.name || u.username || u.email || u.login}</option>)}
              </select>
            </FormField>
            <FormField label="Active">
              <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                <input type="checkbox" checked={!!form.is_active} onChange={(e) => setForm((prev) => ({ ...prev, is_active: e.target.checked }))} style={{ width: 16, height: 16 }} />
                <span style={{ fontSize: 12.5, color: "#475569" }}>Team is active</span>
              </label>
            </FormField>
          </div>
          <FormField label="Description" style={{ marginTop: 12 }}>
            <textarea value={form.description} onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))} placeholder="Optional team description…" rows={3} style={{ ...inputStyle, resize: "vertical" }} />
          </FormField>
          <div style={{ display: "flex", gap: 12, justifyContent: "flex-end", borderTop: "1px solid #e2e7e2", paddingTop: 16, marginTop: 16 }}>
            <button onClick={() => setShowModal(false)} className="ess-btn ess-btn-secondary">Cancel</button>
            <button disabled={saving} onClick={submit} className="ess-btn ess-btn-primary">{saving ? "Saving…" : "Save"}</button>
          </div>
        </div>
      </Modal>

      <Modal open={!!detail} onClose={() => setDetail(null)} title={detail ? `Members — ${detail.name}` : ""} size="md">
        {detail && (
          <div>
            <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
              <select value={newMember} onChange={(e) => setNewMember(e.target.value)} style={{ ...inputStyle, flex: 1 }}>
                <option value="">Add a member…</option>
                {users
                  .filter((u) => !members.some((m) => m.user_id === u.lewoo_uid))
                  .map((u) => <option key={u.id} value={u.id}>{u.name || u.username || u.email || u.login}</option>)}
              </select>
              <button disabled={memberBusy || !newMember} onClick={addMember} className="ess-btn ess-btn-primary">Add</button>
            </div>
            {members.length === 0 ? (
              <EmptyState  title="No members" subtitle="Add salespeople to this team" />
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {members.map((m) => (
                  <div key={m.user_id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 12px", border: "1px solid #e2e7e2", borderRadius: 8 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>{m.employee_name || m.login || m.email}</div>
                      <div style={{ fontSize: 11, color: "#94a3b8" }}>{m.email || m.login}</div>
                    </div>
                    <button title="Remove" onClick={() => removeMember(m)} style={iconBtn("#be123c")}><X size={13} /></button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

const TagPanel = ({ rows, loading, onCreate, onUpdate, onDelete, onToggleActive }) => {
  const toast = useToast();
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const openNew = () => {
    setEditing(null);
    setForm({ name: "", color: "#225f2b", is_active: true });
    setShowModal(true);
  };
  const openEdit = (row) => {
    setEditing(row);
    setForm({ name: row.name || "", color: row.color || "#225f2b", is_active: row.is_active !== false });
    setShowModal(true);
  };
  const submit = async () => {
    if (!form.name.trim()) { toast("Tag name is required", "error"); return; }
    setSaving(true);
    try {
      const payload = { name: form.name.trim(), color: form.color || "#225f2b", is_active: !!form.is_active };
      if (editing) { await onUpdate(editing.id, payload); toast("Updated", "success"); }
      else { await onCreate(payload); toast("Created", "success"); }
      setShowModal(false);
      setEditing(null);
    } catch (e) { toast(e.message || "Save failed", "error"); }
    setSaving(false);
  };

  if (loading) return <LoadingScreen />;

  return (
    <div style={{ background: "white", borderRadius: 8, border: "1px solid #e2e7e2", overflow: "hidden" }}>
      <div style={{ display: "flex", gap: 12, alignItems: "center", padding: 16, borderBottom: "1px solid #e2e7e2" }}>
        <div style={{ fontSize: 13, color: "#64748b" }}>Tags help categorize leads and opportunities.</div>
        <div style={{ marginLeft: "auto" }}>
          <button onClick={openNew} className="ess-btn ess-btn-primary"><Plus size={16} /> New tag</button>
        </div>
      </div>
      {rows.length === 0 ? (
        <EmptyState  title="No tags" subtitle="Create your first tag" />
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "#fafbfa", borderBottom: "1px solid #e2e7e2" }}>
                <th className="ess-th" style={{ minWidth: 80 }}>Color</th>
                <th className="ess-th" style={{ minWidth: 160 }}>Name</th>
                <th className="ess-th" style={{ minWidth: 90 }}>Status</th>
                <th className="ess-th ess-th-r" style={{ minWidth: 150 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => {
                const st = activeBadge(row.is_active);
                return (
                  <tr key={row.id} className="ess-tr" style={{ borderBottom: i < rows.length - 1 ? "1px solid #f1f3f0" : "none" }}>
                    <td className="ess-td">
                      <span title={row.color || ""} style={{ display: "inline-block", width: 20, height: 20, borderRadius: 4, background: row.color || "#e2e7e2", border: "1px solid #cbd5e1", verticalAlign: "middle" }} />
                    </td>
                    <td className="ess-td" style={{ fontWeight: 600, color: "#225f2b" }}>{row.name}</td>
                    <td className="ess-td"><span style={{ background: st.bg, color: st.text, padding: "3px 9px", borderRadius: 6, fontSize: 11, fontWeight: 600 }}>{st.label}</span></td>
                    <td className="ess-td" style={{ whiteSpace: "nowrap", textAlign: "right" }}>
                      <div style={{ display: "flex", gap: 4, justifyContent: "flex-end" }}>
                        <button title="Toggle active" onClick={() => onToggleActive(row)} style={iconBtn("#0f766e")}><Check size={13} /></button>
                        <button title="Edit" onClick={() => openEdit(row)} style={iconBtn("#225f2b")}><Pencil size={13} /></button>
                        <button title="Delete" onClick={() => { if (window.confirm(`Delete tag "${row.name}"?`)) onDelete(row); }} style={iconBtn("#be123c")}><Trash2 size={13} /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <Modal open={showModal} onClose={() => setShowModal(false)} title={editing ? `Edit tag — ${editing.name}` : "New tag"} size="sm">
        <div>
          <div style={{ display: "grid", gap: "12px 16px" }} className="ess-rg-2">
            <FormField label="Tag name" required>
              <input value={form.name} onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))} placeholder="e.g. Urgent" style={inputStyle} />
            </FormField>
            <FormField label="Color">
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <input type="color" value={form.color} onChange={(e) => setForm((prev) => ({ ...prev, color: e.target.value }))} style={{ width: 48, height: 38, cursor: "pointer", border: "1px solid #cbd5e1", borderRadius: 6, padding: 2 }} />
                <span style={{ fontSize: 12, color: "#64748b" }}>{form.color}</span>
              </div>
            </FormField>
          </div>
          <FormField label="Active" style={{ marginTop: 12 }}>
            <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
              <input type="checkbox" checked={!!form.is_active} onChange={(e) => setForm((prev) => ({ ...prev, is_active: e.target.checked }))} style={{ width: 16, height: 16 }} />
              <span style={{ fontSize: 12.5, color: "#475569" }}>Tag is active</span>
            </label>
          </FormField>
          <div style={{ display: "flex", gap: 12, justifyContent: "flex-end", borderTop: "1px solid #e2e7e2", paddingTop: 16, marginTop: 16 }}>
            <button onClick={() => setShowModal(false)} className="ess-btn ess-btn-secondary">Cancel</button>
            <button disabled={saving} onClick={submit} className="ess-btn ess-btn-primary">{saving ? "Saving…" : "Save"}</button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

const LostReasonPanel = ({ rows, loading, onCreate, onUpdate, onDelete, onToggleActive }) => {
  const toast = useToast();
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const openNew = () => {
    setEditing(null);
    setForm({ name: "", is_active: true });
    setShowModal(true);
  };
  const openEdit = (row) => {
    setEditing(row);
    setForm({ name: row.name || "", is_active: row.is_active !== false });
    setShowModal(true);
  };
  const submit = async () => {
    if (!form.name.trim()) { toast("Lost reason is required", "error"); return; }
    setSaving(true);
    try {
      const payload = { name: form.name.trim(), is_active: !!form.is_active };
      if (editing) { await onUpdate(editing.id, payload); toast("Updated", "success"); }
      else { await onCreate(payload); toast("Created", "success"); }
      setShowModal(false);
      setEditing(null);
    } catch (e) { toast(e.message || "Save failed", "error"); }
    setSaving(false);
  };

  if (loading) return <LoadingScreen />;

  return (
    <div style={{ background: "white", borderRadius: 8, border: "1px solid #e2e7e2", overflow: "hidden" }}>
      <div style={{ display: "flex", gap: 12, alignItems: "center", padding: 16, borderBottom: "1px solid #e2e7e2" }}>
        <div style={{ fontSize: 13, color: "#64748b" }}>Reasons used when marking an opportunity as lost.</div>
        <div style={{ marginLeft: "auto" }}>
          <button onClick={openNew} className="ess-btn ess-btn-primary"><Plus size={16} /> New reason</button>
        </div>
      </div>
      {rows.length === 0 ? (
        <EmptyState  title="No lost reasons" subtitle="Create your first reason" />
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "#fafbfa", borderBottom: "1px solid #e2e7e2" }}>
                <th className="ess-th" style={{ minWidth: 200 }}>Reason</th>
                <th className="ess-th" style={{ minWidth: 90 }}>Status</th>
                <th className="ess-th ess-th-r" style={{ minWidth: 150 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => {
                const st = activeBadge(row.is_active);
                return (
                  <tr key={row.id} className="ess-tr" style={{ borderBottom: i < rows.length - 1 ? "1px solid #f1f3f0" : "none" }}>
                    <td className="ess-td" style={{ fontWeight: 600, color: "#225f2b" }}>{row.name}</td>
                    <td className="ess-td"><span style={{ background: st.bg, color: st.text, padding: "3px 9px", borderRadius: 6, fontSize: 11, fontWeight: 600 }}>{st.label}</span></td>
                    <td className="ess-td" style={{ whiteSpace: "nowrap", textAlign: "right" }}>
                      <div style={{ display: "flex", gap: 4, justifyContent: "flex-end" }}>
                        <button title="Toggle active" onClick={() => onToggleActive(row)} style={iconBtn("#0f766e")}><Check size={13} /></button>
                        <button title="Edit" onClick={() => openEdit(row)} style={iconBtn("#225f2b")}><Pencil size={13} /></button>
                        <button title="Delete" onClick={() => { if (window.confirm(`Delete reason "${row.name}"?`)) onDelete(row); }} style={iconBtn("#be123c")}><Trash2 size={13} /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <Modal open={showModal} onClose={() => setShowModal(false)} title={editing ? `Edit lost reason — ${editing.name}` : "New lost reason"} size="sm">
        <div>
          <div style={{ display: "grid", gap: "12px 16px" }} className="ess-rg-2">
            <FormField label="Lost reason" required>
              <input value={form.name} onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))} placeholder="e.g. Price too high" style={inputStyle} />
            </FormField>
            <FormField label="Active">
              <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                <input type="checkbox" checked={!!form.is_active} onChange={(e) => setForm((prev) => ({ ...prev, is_active: e.target.checked }))} style={{ width: 16, height: 16 }} />
                <span style={{ fontSize: 12.5, color: "#475569" }}>Reason is available</span>
              </label>
            </FormField>
          </div>
          <div style={{ display: "flex", gap: 12, justifyContent: "flex-end", borderTop: "1px solid #e2e7e2", paddingTop: 16, marginTop: 16 }}>
            <button onClick={() => setShowModal(false)} className="ess-btn ess-btn-secondary">Cancel</button>
            <button disabled={saving} onClick={submit} className="ess-btn ess-btn-primary">{saving ? "Saving…" : "Save"}</button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

const ActivityTypePanel = ({ rows, loading, onCreate, onUpdate, onDelete, onToggleActive }) => {
  const toast = useToast();
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const openNew = () => {
    setEditing(null);
    setForm({ name: "", delay: 1, is_active: true });
    setShowModal(true);
  };
  const openEdit = (row) => {
    setEditing(row);
    setForm({ name: row.name || "", delay: row.delay ?? 1, is_active: row.is_active !== false });
    setShowModal(true);
  };
  const submit = async () => {
    if (!form.name.trim()) { toast("Activity type name is required", "error"); return; }
    setSaving(true);
    try {
      const payload = { name: form.name.trim(), delay: form.delay !== "" ? parseInt(form.delay) : 1, is_active: !!form.is_active };
      if (editing) { await onUpdate(editing.id, payload); toast("Updated", "success"); }
      else { await onCreate(payload); toast("Created", "success"); }
      setShowModal(false);
      setEditing(null);
    } catch (e) { toast(e.message || "Save failed", "error"); }
    setSaving(false);
  };

  if (loading) return <LoadingScreen />;

  return (
    <div style={{ background: "white", borderRadius: 8, border: "1px solid #e2e7e2", overflow: "hidden" }}>
      <div style={{ display: "flex", gap: 12, alignItems: "center", padding: 16, borderBottom: "1px solid #e2e7e2" }}>
        <div style={{ fontSize: 13, color: "#64748b" }}>Follow-up types available for scheduling activities.</div>
        <div style={{ marginLeft: "auto" }}>
          <button onClick={openNew} className="ess-btn ess-btn-primary"><Plus size={16} /> New type</button>
        </div>
      </div>
      {rows.length === 0 ? (
        <EmptyState  title="No activity types" subtitle="Create your first activity type" />
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "#fafbfa", borderBottom: "1px solid #e2e7e2" }}>
                <th className="ess-th" style={{ minWidth: 180 }}>Type</th>
                <th className="ess-th" style={{ minWidth: 130, textAlign: "right" }}>Delay (days)</th>
                <th className="ess-th" style={{ minWidth: 90 }}>Status</th>
                <th className="ess-th ess-th-r" style={{ minWidth: 150 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => {
                const st = activeBadge(row.is_active);
                return (
                  <tr key={row.id} className="ess-tr" style={{ borderBottom: i < rows.length - 1 ? "1px solid #f1f3f0" : "none" }}>
                    <td className="ess-td" style={{ fontWeight: 600, color: "#225f2b" }}>{row.name}</td>
                    <td className="ess-td" style={{ textAlign: "right", color: "#475569" }}>{row.delay ?? "—"}</td>
                    <td className="ess-td"><span style={{ background: st.bg, color: st.text, padding: "3px 9px", borderRadius: 6, fontSize: 11, fontWeight: 600 }}>{st.label}</span></td>
                    <td className="ess-td" style={{ whiteSpace: "nowrap", textAlign: "right" }}>
                      <div style={{ display: "flex", gap: 4, justifyContent: "flex-end" }}>
                        <button title="Toggle active" onClick={() => onToggleActive(row)} style={iconBtn("#0f766e")}><Check size={13} /></button>
                        <button title="Edit" onClick={() => openEdit(row)} style={iconBtn("#225f2b")}><Pencil size={13} /></button>
                        <button title="Delete" onClick={() => { if (window.confirm(`Delete activity type "${row.name}"?`)) onDelete(row); }} style={iconBtn("#be123c")}><Trash2 size={13} /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <Modal open={showModal} onClose={() => setShowModal(false)} title={editing ? `Edit activity type — ${editing.name}` : "New activity type"} size="sm">
        <div>
          <div style={{ display: "grid", gap: "12px 16px" }} className="ess-rg-2">
            <FormField label="Activity type name" required>
              <input value={form.name} onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))} placeholder="e.g. Call, Meeting, Email" style={inputStyle} />
            </FormField>
            <FormField label="Default delay (days)">
              <input type="number" min="0" value={form.delay} onChange={(e) => setForm((prev) => ({ ...prev, delay: e.target.value }))} placeholder="1" style={inputStyle} />
            </FormField>
          </div>
          <FormField label="Active" style={{ marginTop: 12 }}>
            <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
              <input type="checkbox" checked={!!form.is_active} onChange={(e) => setForm((prev) => ({ ...prev, is_active: e.target.checked }))} style={{ width: 16, height: 16 }} />
              <span style={{ fontSize: 12.5, color: "#475569" }}>Available in activity scheduler</span>
            </label>
          </FormField>
          <div style={{ display: "flex", gap: 12, justifyContent: "flex-end", borderTop: "1px solid #e2e7e2", paddingTop: 16, marginTop: 16 }}>
            <button onClick={() => setShowModal(false)} className="ess-btn ess-btn-secondary">Cancel</button>
            <button disabled={saving} onClick={submit} className="ess-btn ess-btn-primary">{saving ? "Saving…" : "Save"}</button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

const SimplePanel = ({ rows, title, icon, emptyTitle, emptySub, nameField, loading, onCreate, onUpdate, onDelete, onToggleActive }) => {
  const toast = useToast();
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const openNew = () => {
    setEditing(null);
    setForm({ name: "", is_active: true });
    setShowModal(true);
  };
  const openEdit = (row) => {
    setEditing(row);
    setForm({ name: row.name || "", is_active: row.is_active !== false });
    setShowModal(true);
  };
  const submit = async () => {
    if (!form.name.trim()) { toast(`${nameField || "Name"} is required`, "error"); return; }
    setSaving(true);
    try {
      const payload = { name: form.name.trim(), is_active: !!form.is_active };
      if (editing) { await onUpdate(editing.id, payload); toast("Updated", "success"); }
      else { await onCreate(payload); toast("Created", "success"); }
      setShowModal(false);
      setEditing(null);
    } catch (e) { toast(e.message || "Save failed", "error"); }
    setSaving(false);
  };

  if (loading) return <LoadingScreen />;

  return (
    <div style={{ background: "white", borderRadius: 8, border: "1px solid #e2e7e2", overflow: "hidden" }}>
      <div style={{ display: "flex", gap: 12, alignItems: "center", padding: 16, borderBottom: "1px solid #e2e7e2" }}>
        <div style={{ fontSize: 13, color: "#64748b" }}>{title}</div>
        <div style={{ marginLeft: "auto" }}>
          <button onClick={openNew} className="ess-btn ess-btn-primary"><Plus size={16} /> New</button>
        </div>
      </div>
      {rows.length === 0 ? (
        <EmptyState title={emptyTitle || "No entries"} subtitle={emptySub || "Create your first entry"} />
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "#fafbfa", borderBottom: "1px solid #e2e7e2" }}>
                <th className="ess-th" style={{ minWidth: 180 }}>Name</th>
                <th className="ess-th" style={{ minWidth: 90 }}>Status</th>
                <th className="ess-th ess-th-r" style={{ minWidth: 150 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => {
                const st = activeBadge(row.is_active);
                return (
                  <tr key={row.id} className="ess-tr" style={{ borderBottom: i < rows.length - 1 ? "1px solid #f1f3f0" : "none" }}>
                    <td className="ess-td" style={{ fontWeight: 600, color: "#225f2b" }}>{row.name}</td>
                    <td className="ess-td"><span style={{ background: st.bg, color: st.text, padding: "3px 9px", borderRadius: 6, fontSize: 11, fontWeight: 600 }}>{st.label}</span></td>
                    <td className="ess-td" style={{ whiteSpace: "nowrap", textAlign: "right" }}>
                      <div style={{ display: "flex", gap: 4, justifyContent: "flex-end" }}>
                        <button title="Toggle active" onClick={() => onToggleActive(row)} style={iconBtn("#0f766e")}><Check size={13} /></button>
                        <button title="Edit" onClick={() => openEdit(row)} style={iconBtn("#225f2b")}><Pencil size={13} /></button>
                        <button title="Delete" onClick={() => { if (window.confirm(`Delete "${row.name}"?`)) onDelete(row); }} style={iconBtn("#be123c")}><Trash2 size={13} /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <Modal open={showModal} onClose={() => setShowModal(false)} title={editing ? `Edit ${editing.name}` : `New ${nameField || "entry"}`} size="sm">
        <div>
          <div style={{ display: "grid", gap: "12px 16px" }} className="ess-rg-2">
            <FormField label={nameField || "Name"} required>
              <input value={form.name} onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))} placeholder={`e.g. ${nameField || "Entry"}`} style={inputStyle} />
            </FormField>
            <FormField label="Active">
              <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                <input type="checkbox" checked={!!form.is_active} onChange={(e) => setForm((prev) => ({ ...prev, is_active: e.target.checked }))} style={{ width: 16, height: 16 }} />
                <span style={{ fontSize: 12.5, color: "#475569" }}>Entry is active</span>
              </label>
            </FormField>
          </div>
          <div style={{ display: "flex", gap: 12, justifyContent: "flex-end", borderTop: "1px solid #e2e7e2", paddingTop: 16, marginTop: 16 }}>
            <button onClick={() => setShowModal(false)} className="ess-btn ess-btn-secondary">Cancel</button>
            <button disabled={saving} onClick={submit} className="ess-btn ess-btn-primary">{saving ? "Saving…" : "Save"}</button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

const TemplatePanel = ({ rows, loading, onCreate, onUpdate, onDelete, onToggleActive }) => {
  const toast = useToast();
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const bodyRef = useRef(null);

  const [previewLead, setPreviewLead] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [leadsCache, setLeadsCache] = useState([]);

  const VARIABLES = {
    crm_lead: [
      { group: "Lead", vars: [
        { key: "lead.name", label: "Lead name" },
        { key: "lead.type", label: "Type" },
        { key: "lead.stage_name", label: "Stage" },
        { key: "lead.partner_name", label: "Company" },
        { key: "lead.email_from", label: "Email" },
        { key: "lead.phone", label: "Phone" },
        { key: "lead.expected_revenue", label: "Expected revenue" },
        { key: "lead.probability", label: "Probability" },
        { key: "lead.date_deadline", label: "Deadline" },
      ]},
      { group: "Sales", vars: [
        { key: "lead.user_name", label: "Salesperson" },
        { key: "lead.team_name", label: "Team" },
        { key: "salesperson.email", label: "Salesperson email" },
        { key: "salesperson.name", label: "Salesperson name" },
      ]},
    ],
    sales_order: [
      { group: "Order", vars: [
        { key: "sales_order.name", label: "Order name" },
        { key: "sales_order.state", label: "State" },
        { key: "sales_order.amount_total", label: "Amount total" },
        { key: "sales_order.amount_untaxed", label: "Amount untaxed" },
        { key: "sales_order.date_order", label: "Date" },
        { key: "sales_order.valid_until", label: "Valid until" },
      ]},
      { group: "Customer", vars: [
        { key: "sales_order.partner_name", label: "Customer" },
        { key: "sales_order.partner_email", label: "Customer email" },
      ]},
      { group: "Sales", vars: [
        { key: "sales_order.salesperson_name", label: "Salesperson" },
        { key: "sales_order.salesperson_email", label: "Salesperson email" },
        { key: "sales_order.lines_html", label: "Lines HTML" },
      ]},
    ],
  };

  const resolveVariables = (model) => {
    const customModel = !VARIABLES[model];
    if (customModel) {
      return [{ group: "Default (crm_lead)", vars: VARIABLES.crm_lead.flatMap(g => g.vars) }];
    }
    return VARIABLES[model];
  };

  const insertVar = (key) => {
    const textarea = bodyRef.current;
    const field = form.channel_type === "email" ? "body_html" : "body_text";
    const current = form[field] || "";

    if (!textarea) {
      setForm((prev) => ({ ...prev, [field]: (prev[field] || "") + `{{ ${key} }}` }));
      return;
    }
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const next = current.slice(0, start) + `{{ ${key} }}` + current.slice(end);
    setForm((prev) => ({ ...prev, [field]: next }));
    requestAnimationFrame(() => {
      textarea.focus();
      const pos = start + `{{ ${key} }}`.length;
      textarea.setSelectionRange(pos, pos);
    });
  };

  const resolveTemplate = (text, lead) => {
    if (!text || !lead) return text || "";
    const model = form.model || "crm_lead";
    let result = text;

    if (model === "crm_lead" || !VARIABLES[model]) {
      result = result.replace(/\{\{\s*lead\.name\s*\}\}/g, lead.name || "")
        .replace(/\{\{\s*lead\.type\s*\}\}/g, lead.type === "opportunity" ? "Opportunity" : "Lead")
        .replace(/\{\{\s*lead\.stage_name\s*\}\}/g, lead.stage_name || "—")
        .replace(/\{\{\s*lead\.partner_name\s*\}\}/g, lead.partner_name || "—")
        .replace(/\{\{\s*lead\.email_from\s*\}\}/g, lead.email_from || "—")
        .replace(/\{\{\s*lead\.phone\s*\}\}/g, lead.phone || lead.mobile || "—")
        .replace(/\{\{\s*lead\.expected_revenue\s*\}\}/g, lead.expected_revenue != null ? fmt.money(lead.expected_revenue) : "—")
        .replace(/\{\{\s*lead\.probability\s*\}\}/g, lead.probability != null ? `${lead.probability}%` : "—")
        .replace(/\{\{\s*lead\.date_deadline\s*\}\}/g, lead.date_deadline ? fmt.date(lead.date_deadline) : "—")
        .replace(/\{\{\s*lead\.user_name\s*\}\}/g, lead.user_name || "Unassigned")
        .replace(/\{\{\s*lead\.team_name\s*\}\}/g, lead.team_name || "—")
        .replace(/\{\{\s*salesperson\.email\s*\}\}/g, lead.user_email || "—")
        .replace(/\{\{\s*salesperson\.name\s*\}\}/g, lead.user_name || "—");
    } else if (model === "sales_order") {
      result = result.replace(/\{\{\s*sales_order\.name\s*\}\}/g, lead.order_name || "—")
        .replace(/\{\{\s*sales_order\.state\s*\}\}/g, lead.order_state || "—")
        .replace(/\{\{\s*sales_order\.amount_total\s*\}\}/g, lead.amount_total != null ? fmt.money(lead.amount_total) : "—")
        .replace(/\{\{\s*sales_order\.amount_untaxed\s*\}\}/g, lead.amount_untaxed != null ? fmt.money(lead.amount_untaxed) : "—")
        .replace(/\{\{\s*sales_order\.partner_name\s*\}\}/g, lead.partner_name || "—")
        .replace(/\{\{\s*sales_order\.partner_email\s*\}\}/g, lead.partner_email || "—")
        .replace(/\{\{\s*sales_order\.date_order\s*\}\}/g, lead.date_order ? fmt.date(lead.date_order) : "—")
        .replace(/\{\{\s*sales_order\.valid_until\s*\}\}/g, lead.valid_until ? fmt.date(lead.valid_until) : "—")
        .replace(/\{\{\s*sales_order\.salesperson_name\s*\}\}/g, lead.user_name || "—")
        .replace(/\{\{\s*sales_order\.salesperson_email\s*\}\}/g, lead.user_email || "—")
        .replace(/\{\{\s*sales_order\.lines_html\s*\}\}/g, lead.lines_html || "");
    }
    return result;
  };

  const loadPreviewLead = async (leadId) => {
    if (!leadId) { setPreviewLead(null); return; }
    setPreviewLoading(true);
    try {
      const d = await api.crm.get(leadId);
      setPreviewLead(d);
    } catch (e) {
      toast(e.message || "Failed to load lead", "error");
      setPreviewLead(null);
    }
    setPreviewLoading(false);
  };

  const openNew = () => {
    setEditing(null);
    setForm({ name: "", model: "crm_lead", channel_type: "email", subject: "", body_html: "", body_text: "", attach_documents: false, lang: "en", is_active: true });
    setPreviewLead(null);
    setShowModal(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setForm({ name: row.name || "", model: row.model || "crm_lead", channel_type: row.channel_type || "email", subject: row.subject || "", body_html: row.body_html || "", body_text: row.body_text || "", attach_documents: !!row.attach_documents, lang: row.lang || "en", is_active: row.is_active !== false });
    setPreviewLead(null);
    setShowModal(true);
  };

  const submit = async () => {
    if (!form.name.trim()) { toast("Template name is required", "error"); return; }
    if (!form.model.trim()) { toast("Model is required", "error"); return; }
    if (form.channel_type === "email" && !form.subject.trim()) { toast("Subject is required for email templates", "error"); return; }
    if (!form.body_html.trim() && !form.body_text.trim()) { toast("Body is required", "error"); return; }
    setSaving(true);
    try {
      const payload = { name: form.name.trim(), model: form.model.trim() || "crm_lead", channel_type: form.channel_type || "email", subject: form.subject.trim(), body_html: form.body_html.trim(), body_text: form.body_text.trim(), attach_documents: !!form.attach_documents, lang: form.lang || "en", is_active: !!form.is_active };
      if (editing) { await onUpdate(editing.id, payload); toast("Updated", "success"); }
      else { await onCreate(payload); toast("Created", "success"); }
      setShowModal(false);
      setEditing(null);
      setPreviewLead(null);
    } catch (e) { toast(e.message || "Save failed", "error"); }
    setSaving(false);
  };

  const bodyField = form.channel_type === "email" ? form.body_html : form.body_text;
  const bodyLength = (bodyField || "").length;
  const smsOverLimit = form.channel_type === "sms" && bodyLength > 160;

  if (loading) return <LoadingScreen />;

  return (
    <div style={{ background: "white", borderRadius: 8, border: "1px solid #e2e7e2", overflow: "hidden" }}>
      <div style={{ display: "flex", gap: 12, alignItems: "center", padding: 16, borderBottom: "1px solid #e2e7e2" }}>
        <div style={{ fontSize: 13, color: "#64748b" }}>Templates for CRM messages (email, WhatsApp, SMS). Variables like {'{{ lead.name }}'} are resolved per send.</div>
        <div style={{ marginLeft: "auto" }}>
          <button onClick={openNew} className="ess-btn ess-btn-primary"><Plus size={16} /> New template</button>
        </div>
      </div>
      {rows.length === 0 ? (
        <EmptyState  title="No message templates" subtitle="Create your first template" />
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "#fafbfa", borderBottom: "1px solid #e2e7e2" }}>
                <th className="ess-th" style={{ minWidth: 180 }}>Name</th>
                <th className="ess-th" style={{ minWidth: 200 }}>Subject</th>
                <th className="ess-th" style={{ minWidth: 100 }}>Model</th>
                <th className="ess-th" style={{ minWidth: 100 }}>Channel</th>
                <th className="ess-th" style={{ minWidth: 100 }}>Language</th>
                <th className="ess-th" style={{ minWidth: 80 }}>Attachments</th>
                <th className="ess-th" style={{ minWidth: 90 }}>Status</th>
                <th className="ess-th ess-th-r" style={{ minWidth: 150 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => {
                const st = activeBadge(row.is_active);
                const ch = row.channel_type === "whatsapp" ? { label: "WhatsApp", bg: "#f0fdf4", color: "#166534" } : row.channel_type === "sms" ? { label: "SMS", bg: "#f8fafc", color: "#475569" } : { label: "Email", bg: "#eff6ff", color: "#1e40af" };
                return (
                  <tr key={row.id} className="ess-tr" style={{ borderBottom: i < rows.length - 1 ? "1px solid #f1f3f0" : "none" }}>
                    <td className="ess-td" style={{ fontWeight: 600, color: "#225f2b" }}>{row.name}</td>
                    <td className="ess-td" style={{ maxWidth: 280, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: "#475569" }}>{row.subject || "—"}</td>
                    <td className="ess-td" style={{ color: "#64748b", whiteSpace: "nowrap" }}>{row.model || "crm_lead"}</td>
                    <td className="ess-td" style={{ whiteSpace: "nowrap" }}>
                      <span style={{ background: ch.bg, color: ch.color, padding: "3px 9px", borderRadius: 6, fontSize: 11, fontWeight: 600 }}>{ch.label}</span>
                    </td>
                    <td className="ess-td" style={{ color: "#64748b", whiteSpace: "nowrap" }}>{row.lang === "fr" ? "Français" : "English"}</td>
                    <td className="ess-td">
                      {row.attach_documents ? (
                        <span style={{ fontSize: 11, color: "#166534", background: "#f0fdf4", padding: "2px 8px", borderRadius: 5, fontWeight: 600 }}>Lead docs</span>
                      ) : (
                        <span style={{ fontSize: 11, color: "#94a3b8" }}>—</span>
                      )}
                    </td>
                    <td className="ess-td"><span style={{ background: st.bg, color: st.text, padding: "3px 9px", borderRadius: 6, fontSize: 11, fontWeight: 600 }}>{st.label}</span></td>
                    <td className="ess-td" style={{ whiteSpace: "nowrap", textAlign: "right" }}>
                      <div style={{ display: "flex", gap: 4, justifyContent: "flex-end" }}>
                        <button title="Toggle active" onClick={() => onToggleActive(row)} style={iconBtn("#0f766e")}><Check size={13} /></button>
                        <button title="Edit" onClick={() => openEdit(row)} style={iconBtn("#225f2b")}><Pencil size={13} /></button>
                        <button title="Delete" onClick={() => { if (window.confirm(`Delete template "${row.name}"?`)) onDelete(row); }} style={iconBtn("#be123c")}><Trash2 size={13} /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <Modal open={showModal} onClose={() => setShowModal(false)} title={editing ? `Edit template — ${editing.name}` : "New template"} size="lg">
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          {/* === SECTION: Configuration === */}
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#225f2b", textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 12, display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ width: 16, height: 16, background: "#225f2b", borderRadius: 3, display: "inline-flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 9 }}>1</span>
              Configuration
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px 16px" }}>
              <div>
                <label style={labelStyle}>Template name <span style={{ color: "#be123c" }}>*</span></label>
                <input value={form.name} onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))} placeholder="e.g. Welcome email" style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>Model</label>
                <input value={form.model} onChange={(e) => setForm((prev) => ({ ...prev, model: e.target.value }))} placeholder="crm_lead, sales_order, ..." style={inputStyle} list="model-suggestions" />
                <datalist id="model-suggestions">
                  <option value="crm_lead" />
                  <option value="sales_order" />
                </datalist>
              </div>
              <div>
                <label style={labelStyle}>Channel</label>
                <select value={form.channel_type} onChange={(e) => setForm((prev) => ({ ...prev, channel_type: e.target.value }))} style={inputStyle}>
                  <option value="email">Email</option>
                  <option value="whatsapp">WhatsApp</option>
                  <option value="sms">SMS</option>
                </select>
              </div>
              <div>
                <label style={labelStyle}>Language</label>
                <select value={form.lang || "en"} onChange={(e) => setForm((prev) => ({ ...prev, lang: e.target.value }))} style={inputStyle}>
                  <option value="en">English</option>
                  <option value="fr">Français</option>
                </select>
              </div>
            </div>
            {form.channel_type === "email" && (
              <div style={{ marginTop: 12 }}>
                <label style={labelStyle}>Subject <span style={{ color: "#be123c" }}>*</span></label>
                <input value={form.subject} onChange={(e) => setForm((prev) => ({ ...prev, subject: e.target.value }))} placeholder="Hello {{ lead.name }}!" style={inputStyle} />
              </div>
            )}
          </div>

          {/* === SECTION: Body Editor === */}
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#225f2b", textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 12, display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ width: 16, height: 16, background: "#225f2b", borderRadius: 3, display: "inline-flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 9 }}>2</span>
              Message body
            </div>
            <div style={{ border: "1px solid #e2e7e2", borderRadius: 8, overflow: "hidden" }}>
              {/* Toolbar */}
              <div style={{ display: "flex", alignItems: "center", gap: 4, padding: "8px 10px", background: "#f8fafc", borderBottom: "1px solid #e2e7e2", flexWrap: "wrap" }}>
                <span style={{ fontSize: 11, fontWeight: 600, color: "#64748b", marginRight: 4 }}>Insert:</span>
                {resolveVariables(form.model).map((group) => (
                  <div key={group.group} style={{ position: "relative" }}>
                    <select
                      onChange={(e) => { if (e.target.value) { insertVar(e.target.value); e.target.value = ""; } }}
                      style={{ padding: "3px 8px", fontSize: 11, borderRadius: 5, border: "1px solid #cbd5e1", background: "#fff", color: "#334155", cursor: "pointer", maxWidth: 120 }}
                      defaultValue=""
                    >
                      <option value="" disabled>{group.group}</option>
                      {group.vars.map((v) => (
                        <option key={v.key} value={v.key}>{v.label}</option>
                      ))}
                    </select>
                  </div>
                ))}
                {!VARIABLES[form.model] && (
                  <span style={{ fontSize: 10.5, color: "#b45309", fontStyle: "italic" }}>
                    Custom model — using crm_lead variables
                  </span>
                )}
              </div>
              {/* Textarea */}
              <textarea
                ref={bodyRef}
                value={form.channel_type === "email" ? form.body_html : form.body_text}
                onChange={(e) => setForm((prev) => ({ ...prev, [form.channel_type === "email" ? "body_html" : "body_text"]: e.target.value }))}
                placeholder={form.channel_type === "email" ? "<p>Hello {{ lead.name }},</p><p>Your order {{ sales_order.name }} is ready.</p>" : "Hello {{ lead.name }}, your order is ready."}
                rows={10}
                maxLength={form.channel_type === "sms" ? 160 : undefined}
                style={{ width: "100%", padding: "12px 14px", border: "none", outline: "none", resize: "vertical", fontFamily: form.channel_type === "email" ? "monospace" : "inherit", fontSize: 13, lineHeight: 1.6, background: "#fff", boxSizing: "border-box" }}
              />
              {/* Footer */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 10px", background: "#f8fafc", borderTop: "1px solid #e2e7e2" }}>
                <div style={{ fontSize: 11, color: "#94a3b8" }}>
                  {form.channel_type === "email" ? "HTML supported" : "Plain text"}
                </div>
                {form.channel_type === "sms" && (
                  <div style={{ fontSize: 11, fontWeight: 600, color: smsOverLimit ? "#be123c" : bodyLength > 140 ? "#b45309" : "#94a3b8" }}>
                    {bodyLength}/160{smsOverLimit ? " — OVER LIMIT" : bodyLength > 140 ? " — Warning" : ""}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* === SECTION: Attachments (email only) === */}
          {form.channel_type === "email" && (
            <div style={{ background: "#f8faf8", border: "1px solid #e2e7e2", borderRadius: 8, padding: "12px 14px" }}>
              <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                <input type="checkbox" checked={!!form.attach_documents} onChange={(e) => setForm((prev) => ({ ...prev, attach_documents: e.target.checked }))} style={{ width: 16, height: 16 }} />
                <span style={{ fontSize: 13, fontWeight: 600, color: "#334155" }}>Attach lead documents</span>
              </label>
              <div style={{ fontSize: 11.5, color: "#64748b", marginTop: 4, paddingLeft: 24 }}>
                When enabled, files linked to the lead (quotes, invoices, attachments) will be automatically attached to sent emails.
              </div>
            </div>
          )}

          {/* === SECTION: Preview === */}
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#225f2b", textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 12, display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ width: 16, height: 16, background: "#225f2b", borderRadius: 3, display: "inline-flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 9 }}>3</span>
              Live preview
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
              <select
                value={previewLead?.id || ""}
                onChange={(e) => loadPreviewLead(e.target.value)}
                style={{ ...inputStyle, flex: 1 }}
              >
                <option value="">Select a lead to preview with real data…</option>
                {leadsCache.map((l) => (
                  <option key={l.id} value={l.id}>{l.name} — {l.partner_name || l.email_from || `#${l.id}`}</option>
                ))}
              </select>
              <button
                onClick={async () => {
                  try {
                    const res = await api.crm.list({ limit: 50, sort_by: "date_open", sort_dir: "desc" });
                    const n = norm(res);
                    const leads = Array.isArray(n) ? n : Array.isArray(n?.data) ? n?.data : [];
                    setLeadsCache(leads);
                    toast(`${leads.length} leads loaded`, "success");
                  } catch (e) {
                    toast(e.message || "Failed to load leads", "error");
                  }
                }}
                className="ess-btn ess-btn-secondary"
                style={{ whiteSpace: "nowrap" }}
              >
                <Search size={14} /> Load leads
              </button>
            </div>

            {previewLoading && (
              <div style={{ border: "1px solid #e2e7e2", borderRadius: 8, padding: 24, textAlign: "center", fontSize: 12, color: "#94a3b8" }}>
                Loading lead data…
              </div>
            )}

            {!previewLoading && !previewLead && (
              <div style={{ border: "1px dashed #cbd5e1", borderRadius: 8, padding: 24, textAlign: "center" }}>
                <div style={{ fontSize: 24, marginBottom: 8 }}>👁️</div>
                <div style={{ fontSize: 12, color: "#64748b" }}>Select a lead above to see how your template will look with real data.</div>
              </div>
            )}

            {!previewLoading && previewLead && (
              <div style={{ border: "1px solid #e2e7e2", borderRadius: 8, overflow: "hidden" }}>
                {form.channel_type === "email" && (
                  <div>
                    <div style={{ padding: "12px 16px", background: "#f1f5f9", borderBottom: "1px solid #e2e7e2" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                        <span style={{ fontSize: 10, fontWeight: 700, color: "#64748b", textTransform: "uppercase" }}>Subject</span>
                      </div>
                      <div style={{ fontSize: 14, fontWeight: 600, color: "#1e293b" }}>{resolveTemplate(form.subject, previewLead) || "—"}</div>
                    </div>
                    <div style={{ padding: 16, background: "#fff", maxHeight: 260, overflowY: "auto" }}>
                      <div style={{ fontSize: 13, color: "#334155", lineHeight: 1.6 }} dangerouslySetInnerHTML={{ __html: resolveTemplate(form.body_html, previewLead) || "<em style='color:#94a3b8'>Empty body — add content above</em>" }} />
                    </div>
                    {form.attach_documents && (
                      <div style={{ padding: "10px 16px", background: "#f0fdf4", borderTop: "1px solid #bbf7d0", fontSize: 11, color: "#166534", display: "flex", alignItems: "center", gap: 6 }}>
                        <span>📎</span> Lead documents will be attached
                      </div>
                    )}
                  </div>
                )}
                {form.channel_type === "whatsapp" && (
                  <div style={{ background: "#f0fdf4", padding: 16 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                      <span style={{ width: 24, height: 24, borderRadius: "50%", background: "#25d366", display: "inline-flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 12 }}>✓</span>
                      <span style={{ fontSize: 12, fontWeight: 600, color: "#166534" }}>WhatsApp Message</span>
                    </div>
                    <div style={{ background: "#fff", borderRadius: 12, padding: "12px 16px", fontSize: 13, color: "#334155", whiteSpace: "pre-wrap", lineHeight: 1.6, boxShadow: "0 1px 2px rgba(0,0,0,0.05)" }}>
                      {resolveTemplate(form.body_text, previewLead) || <em style={{ color: "#94a3b8" }}>Empty message</em>}
                    </div>
                  </div>
                )}
                {form.channel_type === "sms" && (
                  <div style={{ background: "#f8fafc", padding: 16 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                      <span style={{ fontSize: 12, fontWeight: 600, color: "#475569" }}>SMS</span>
                      <span style={{ fontSize: 10, color: "#94a3b8", background: "#e2e8f0", padding: "2px 8px", borderRadius: 10 }}>{bodyLength} chars</span>
                    </div>
                    <div style={{ background: "#fff", borderRadius: 8, padding: "12px 16px", fontSize: 13, color: "#334155", whiteSpace: "pre-wrap", lineHeight: 1.6, border: "1px solid #e2e7e2" }}>
                      {resolveTemplate(form.body_text, previewLead) || <em style={{ color: "#94a3b8" }}>Empty message</em>}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* === SECTION: Status & Actions === */}
          <div style={{ borderTop: "1px solid #e2e7e2", paddingTop: 16 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                <input type="checkbox" checked={!!form.is_active} onChange={(e) => setForm((prev) => ({ ...prev, is_active: e.target.checked }))} style={{ width: 16, height: 16 }} />
                <span style={{ fontSize: 13, color: "#334155" }}>Template is active</span>
              </label>
              <div style={{ display: "flex", gap: 10 }}>
                <button onClick={() => setShowModal(false)} className="ess-btn ess-btn-secondary">Cancel</button>
                <button disabled={saving} onClick={submit} className="ess-btn ess-btn-primary">{saving ? "Saving…" : "Save template"}</button>
              </div>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};

const SmtpPanel = () => {
  const toast = useToast();
  const [form, setForm] = useState(null);
  const [hasPassword, setHasPassword] = useState(false);
  const [saving, setSaving] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testTo, setTestTo] = useState("");

  useEffect(() => {
    api.mail.smtp
      .get()
      .then((d) => {
        setHasPassword(!!d?.has_password);
        setForm({
          host: d?.host || "",
          port: d?.port ?? 587,
          secure: !!d?.secure,
          username: d?.username || "",
          password: "",
          from_name: d?.from_name || "",
          from_email: d?.from_email || "",
          is_active: d ? d.is_active !== false : true,
        });
      })
      .catch((e) => {
        toast(e.message || "Failed to load SMTP config", "error");
        setForm({ host: "", port: 587, secure: false, username: "", password: "", from_name: "", from_email: "", is_active: true });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async () => {
    if (!form.host.trim()) { toast("Host is required", "error"); return; }
    setSaving(true);
    try {
      await api.mail.smtp.update({
        host: form.host.trim(),
        port: form.port !== "" && form.port != null ? parseInt(form.port) : undefined,
        secure: !!form.secure,
        username: form.username.trim() || undefined,
        password: form.password || undefined,
        from_name: form.from_name.trim() || undefined,
        from_email: form.from_email.trim() || undefined,
        is_active: !!form.is_active,
      });
      setForm((p) => ({ ...p, password: "" }));
      setHasPassword(true);
      toast("SMTP configuration saved", "success");
    } catch (e) { toast(e.message || "Save failed", "error"); }
    setSaving(false);
  };

  const verify = async () => {
    setVerifying(true);
    try {
      await api.mail.smtp.verify();
      toast("SMTP connection successful", "success");
    } catch (e) { toast(e.message || "SMTP verification failed", "error"); }
    setVerifying(false);
  };

  const sendTest = async () => {
    if (!testTo.trim()) { toast("Enter a recipient address", "error"); return; }
    setTesting(true);
    try {
      await api.mail.smtp.test(testTo.trim());
      toast(`Test email sent to ${testTo.trim()}`, "success");
    } catch (e) { toast(e.message || "Test failed", "error"); }
    setTesting(false);
  };

  if (!form) return <LoadingScreen />;

  return (
    <div style={{ background: "white", borderRadius: 8, border: "1px solid #e2e7e2" }}>
      <div style={{ display: "flex", gap: 12, alignItems: "center", padding: 16, borderBottom: "1px solid #e2e7e2", flexWrap: "wrap" }}>
        <div style={{ fontSize: 13, color: "#64748b" }}>
          Outgoing mail server used by CRM notifications. The password is stored encrypted.
        </div>
        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: "#334155" }}>Active</span>
          <input type="checkbox" checked={!!form.is_active} onChange={(e) => setForm((p) => ({ ...p, is_active: e.target.checked }))} style={{ width: 18, height: 18, cursor: "pointer" }} />
        </div>
      </div>
      <div style={{ padding: 20, maxWidth: 720 }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 160px", gap: 14 }}>
          <FormField label="Host" required>
            <input value={form.host} onChange={(e) => setForm((p) => ({ ...p, host: e.target.value }))} placeholder="smtp.gmail.com" style={inputStyle} />
          </FormField>
          <FormField label="Port">
            <input type="number" value={form.port ?? ""} onChange={(e) => setForm((p) => ({ ...p, port: e.target.value }))} placeholder="587" style={inputStyle} />
          </FormField>
          <FormField label="Username">
            <input value={form.username} onChange={(e) => setForm((p) => ({ ...p, username: e.target.value }))} placeholder="no-reply@example.com" style={inputStyle} />
          </FormField>
          <FormField label="Password">
            <input type="password" value={form.password} onChange={(e) => setForm((p) => ({ ...p, password: e.target.value }))} placeholder={hasPassword ? "•••••••• (unchanged)" : "Password"} style={inputStyle} />
          </FormField>
          <FormField label="From name">
            <input value={form.from_name} onChange={(e) => setForm((p) => ({ ...p, from_name: e.target.value }))} placeholder="CRM Notifications" style={inputStyle} />
          </FormField>
          <FormField label="From email">
            <input value={form.from_email} onChange={(e) => setForm((p) => ({ ...p, from_email: e.target.value }))} placeholder="no-reply@example.com" style={inputStyle} />
          </FormField>
        </div>
        <label style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 4, cursor: "pointer" }}>
          <input type="checkbox" checked={!!form.secure} onChange={(e) => setForm((p) => ({ ...p, secure: e.target.checked }))} style={{ width: 18, height: 18 }} />
          <span style={{ fontSize: 13, color: "#334155", fontWeight: 600 }}>Use TLS/SSL (port 465)</span>
        </label>

        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", borderTop: "1px solid #e2e7e2", paddingTop: 20, marginTop: 20 }}>
          <button disabled={saving} onClick={save} className="ess-btn ess-btn-primary">{saving ? "Saving…" : "Save configuration"}</button>
          <button disabled={verifying} onClick={verify} className="ess-btn ess-btn-secondary">{verifying ? "Checking…" : "Verify connection"}</button>
          <div style={{ display: "flex", gap: 6, marginLeft: "auto", alignItems: "center" }}>
            <input value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="recipient@example.com" style={{ ...inputStyle, width: 220 }} />
            <button disabled={testing} onClick={sendTest} className="ess-btn ess-btn-secondary"><Send size={14} /> {testing ? "Sending…" : "Send test"}</button>
          </div>
        </div>
      </div>
    </div>
  );
};

const TRIGGER_LABELS = {
  on_create: "Lead created",
  on_stage_change: "Stage changed",
  on_assign: "Salesperson assigned",
  on_won: "Opportunity won",
  on_lost: "Opportunity lost",
  on_update: "Lead updated",
};

const RECIPIENT_LABELS = {
  lead_email: "Customer (lead email)",
  partner_email: "Partner / Company email",
  salesperson_email: "Salesperson",
  team_leader_email: "Team leader",
  custom: "Custom address",
  lead_phone: "Customer (lead phone)",
  lead_mobile: "Customer (lead mobile)",
  partner_phone: "Partner phone",
  salesperson_phone: "Salesperson phone",
  team_leader_phone: "Team leader phone",
};

const CHANGED_FIELD_LABELS = {
  name: "Name",
  expected_revenue: "Expected revenue",
  probability: "Probability",
  priority: "Priority",
  date_deadline: "Deadline",
  partner_id: "Customer",
  user_id: "Salesperson",
  team_id: "Team",
  email_from: "Email",
  phone: "Phone",
};

const normalizeAllowedFields = (allowed) => {
  let entries = [];
  if (Array.isArray(allowed)) {
    entries = allowed.map((a) =>
      typeof a === "string" ? { value: a } : { value: a?.value ?? a?.name ?? a?.field, label: a?.label }
    );
  } else if (allowed && typeof allowed === "object") {
    entries = Object.entries(allowed).map(([value, label]) => ({ value, label: typeof label === "string" ? label : undefined }));
  }
  return entries
    .filter((e) => e.value)
    .map((e) => ({ value: e.value, label: e.label || CHANGED_FIELD_LABELS[e.value] || e.value }));
};

const RulesPanel = ({ rows, templates, stages, teams, users, lostReasons, changedFieldsAllowed = [], loading, onCreate, onUpdate, onDelete, onToggleActive }) => {
  const toast = useToast();
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const fieldOptions =
    changedFieldsAllowed.length > 0
      ? changedFieldsAllowed
      : Object.entries(CHANGED_FIELD_LABELS).map(([value, label]) => ({ value, label }));

  const openNew = () => {
    setEditing(null);
    setForm({
      name: "", trigger_type: "on_create", template_id: "",
      recipients: ["lead_email"], custom_email: "", custom_phone: "",
      cond_to_stage_id: "", cond_from_stage_id: "", cond_team_id: "", cond_user_id: "", cond_priority: "", cond_lost_reason_id: "",
      changed_fields: [],
      is_active: true,
    });
    setShowModal(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    const c = row.conditions || {};
    const rec = Array.isArray(row.recipients) ? row.recipients : [];
    const selectedTemplate = templates.find((t) => String(t.id) === String(row.template_id));
    const isWhatsApp = selectedTemplate?.channel_type === "whatsapp";
    setForm({
      name: row.name || "",
      trigger_type: row.trigger_type || "on_create",
      template_id: row.template_id ?? "",
      recipients: rec.filter((r) => {
        if (isWhatsApp) return r.includes("_phone") || r === "custom";
        return !r.includes("_phone");
      }),
      custom_email: isWhatsApp ? "" : (row.custom_email || ""),
      custom_phone: isWhatsApp ? (row.custom_phone || "") : "",
      cond_to_stage_id: c.to_stage_id ?? "",
      cond_from_stage_id: c.from_stage_id ?? "",
      cond_team_id: c.team_id ?? "",
      cond_user_id: c.user_id ?? "",
      cond_priority: c.priority ?? "",
      cond_lost_reason_id: c.lost_reason_id ?? "",
      changed_fields: Array.isArray(c.changed_fields) ? c.changed_fields.filter((f) => fieldOptions.some((o) => o.value === f)) : [],
      is_active: row.is_active !== false,
    });
    setShowModal(true);
  };

  const openPreset = (preset) => {
    setEditing(null);
    const presets = {
      welcome: { name: "Welcome email on lead creation", trigger_type: "on_create", template_id: "", recipients: ["lead_email"], custom_email: "", cond_from_stage_id: "", cond_to_stage_id: "", cond_team_id: "", cond_user_id: "", cond_priority: "", cond_lost_reason_id: "", changed_fields: [], is_active: true },
      stage_change: { name: "Notify salesperson on stage change", trigger_type: "on_stage_change", template_id: "", recipients: ["salesperson_email"], custom_email: "", cond_from_stage_id: "", cond_to_stage_id: "", cond_team_id: "", cond_user_id: "", cond_priority: "", cond_lost_reason_id: "", changed_fields: [], is_active: true },
      won: { name: "Notify salesperson on opportunity won", trigger_type: "on_won", template_id: "", recipients: ["salesperson_email"], custom_email: "", cond_from_stage_id: "", cond_to_stage_id: "", cond_team_id: "", cond_user_id: "", cond_priority: "", cond_lost_reason_id: "", changed_fields: [], is_active: true },
      lost: { name: "Notify customer on opportunity lost", trigger_type: "on_lost", template_id: "", recipients: ["lead_email"], custom_email: "", cond_from_stage_id: "", cond_to_stage_id: "", cond_team_id: "", cond_user_id: "", cond_priority: "", cond_lost_reason_id: "", changed_fields: [], is_active: true },
    };
    setForm(presets[preset] || presets.welcome);
    setShowModal(true);
  };

  const toggleRecipient = (key) => {
    setForm((prev) => {
      const has = prev.recipients.includes(key);
      return { ...prev, recipients: has ? prev.recipients.filter((r) => r !== key) : [...prev.recipients, key] };
    });
  };

  const toggleChangedField = (key) => {
    setForm((prev) => {
      const has = prev.changed_fields.includes(key);
      return { ...prev, changed_fields: has ? prev.changed_fields.filter((f) => f !== key) : [...prev.changed_fields, key] };
    });
  };

  const submit = async () => {
    if (!form.name.trim()) { toast("Rule name is required", "error"); return; }
    if (!form.template_id) { toast("Template is required", "error"); return; }
    if (form.recipients.length === 0) { toast("Select at least one recipient", "error"); return; }
    
    const selectedTemplate = templates.find((t) => String(t.id) === String(form.template_id));
    const isWhatsApp = selectedTemplate?.channel_type === "whatsapp";
    
    if (isWhatsApp) {
      const hasPhoneRecipient = form.recipients.some((r) => r.includes("_phone"));
      if (!hasPhoneRecipient) { toast("Select at least one phone recipient for WhatsApp", "error"); return; }
      if (form.recipients.includes("custom") && !form.custom_phone?.trim()) { toast("Custom phone number is required", "error"); return; }
    } else {
      if (form.recipients.includes("custom") && !form.custom_email?.trim()) { toast("Custom email address is required", "error"); return; }
    }
    
    setSaving(true);
    try {
      const conditions = {};
      if (form.trigger_type === "on_update" && Array.isArray(form.changed_fields) && form.changed_fields.length) {
        conditions.changed_fields = [...form.changed_fields];
      }
      if (form.cond_to_stage_id !== "" && form.cond_to_stage_id != null) conditions.to_stage_id = parseInt(form.cond_to_stage_id);
      if (form.cond_from_stage_id !== "" && form.cond_from_stage_id != null) conditions.from_stage_id = parseInt(form.cond_from_stage_id);
      if (form.cond_team_id !== "" && form.cond_team_id != null) conditions.team_id = parseInt(form.cond_team_id);
      if (form.cond_user_id !== "" && form.cond_user_id != null) conditions.user_id = parseInt(form.cond_user_id);
      if (form.cond_priority !== "" && form.cond_priority != null) conditions.priority = parseInt(form.cond_priority);
      if (form.cond_lost_reason_id !== "" && form.cond_lost_reason_id != null) conditions.lost_reason_id = parseInt(form.cond_lost_reason_id);
      const payload = {
        name: form.name.trim(),
        trigger_type: form.trigger_type,
        template_id: parseInt(form.template_id),
        recipients: form.recipients,
        custom_email: !isWhatsApp && form.recipients.includes("custom") ? form.custom_email.trim() : undefined,
        custom_phone: isWhatsApp && form.recipients.includes("custom") ? form.custom_phone.trim() : undefined,
        conditions: Object.keys(conditions).length ? conditions : undefined,
        is_active: !!form.is_active,
      };
      if (editing) { await onUpdate(editing.id, payload); toast("Updated", "success"); }
      else { await onCreate(payload); toast("Created", "success"); }
      setShowModal(false);
      setEditing(null);
    } catch (e) { toast(e.message || "Save failed", "error"); }
    setSaving(false);
  };

  const recipientText = (row) => {
    const rec = Array.isArray(row.recipients) ? row.recipients : [];
    return rec.map((r) => {
      if (r === "custom") return row.custom_phone || row.custom_email || "Custom";
      return RECIPIENT_LABELS[r] || r;
    }).join(", ") || "—";
  };

  if (loading) return <LoadingScreen />;

  return (
    <div style={{ background: "white", borderRadius: 8, border: "1px solid #e2e7e2", overflow: "hidden" }}>
      <div style={{ display: "flex", gap: 12, alignItems: "center", padding: 16, borderBottom: "1px solid #e2e7e2" }}>
        <div style={{ fontSize: 13, color: "#64748b" }}>
          Automatic emails sent when CRM events happen (create, stage change, assign, won, lost).
        </div>
        <div style={{ marginLeft: "auto", display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button onClick={openNew} className="ess-btn ess-btn-primary"><Plus size={16} /> New rule</button>
          <button onClick={() => openPreset("welcome")} className="ess-btn ess-btn-secondary" title="Create a welcome email sent when a lead is created">Welcome lead</button>
          <button onClick={() => openPreset("stage_change")} className="ess-btn ess-btn-secondary" title="Notify the salesperson when a lead moves to a new stage">Stage change</button>
          <button onClick={() => openPreset("won")} className="ess-btn ess-btn-secondary" title="Notify the salesperson when an opportunity is won">Won</button>
          <button onClick={() => openPreset("lost")} className="ess-btn ess-btn-secondary" title="Notify the customer when an opportunity is lost">Lost</button>
        </div>
      </div>
      {rows.length === 0 ? (
        <EmptyState  title="No automation rules" subtitle="Create a rule to send automatic emails on CRM events" />
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "#fafbfa", borderBottom: "1px solid #e2e7e2" }}>
                <th className="ess-th" style={{ minWidth: 180 }}>Name</th>
                <th className="ess-th" style={{ minWidth: 170 }}>Trigger</th>
                <th className="ess-th" style={{ minWidth: 180 }}>Template</th>
                <th className="ess-th" style={{ minWidth: 240 }}>Recipients</th>
                <th className="ess-th" style={{ minWidth: 90 }}>Status</th>
                <th className="ess-th ess-th-r" style={{ minWidth: 150 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => {
                const st = activeBadge(row.is_active);
                const inactive = row.is_active === false;
                return (
                  <tr key={row.id} className="ess-tr" style={{ borderBottom: i < rows.length - 1 ? "1px solid #f1f3f0" : "none", opacity: inactive ? 0.6 : 1, background: inactive ? "#fafbfa" : "transparent" }}>
                    <td className="ess-td" style={{ fontWeight: 600, color: "#225f2b", textDecoration: inactive ? "line-through" : "none" }}>{row.name}</td>
                    <td className="ess-td" style={{ whiteSpace: "nowrap" }}>
                      <span style={{ background: inactive ? "#f1f5f9" : "#eef6f3", color: inactive ? "#94a3b8" : "#0f766e", padding: "3px 9px", borderRadius: 6, fontSize: 11, fontWeight: 600 }}>{TRIGGER_LABELS[row.trigger_type] || row.trigger_type}</span>
                    </td>
                    <td className="ess-td" style={{ color: inactive ? "#94a3b8" : "#475569" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <span>{templates.find((t) => t.id === row.template_id)?.name || row.template_id}</span>
                        {(() => {
                          const tpl = templates.find((t) => t.id === row.template_id);
                          const ch = tpl?.channel_type === "whatsapp" ? { label: "WhatsApp", bg: "#f0fdf4", color: "#166534" } : tpl?.channel_type === "sms" ? { label: "SMS", bg: "#f8fafc", color: "#475569" } : tpl?.channel_type === "email" ? { label: "Email", bg: "#eff6ff", color: "#1e40af" } : null;
                          return ch ? <span style={{ background: ch.bg, color: ch.color, padding: "2px 7px", borderRadius: 5, fontSize: 10, fontWeight: 600 }}>{ch.label}</span> : null;
                        })()}
                      </div>
                    </td>
                    <td className="ess-td" style={{ color: inactive ? "#94a3b8" : "#64748b" }}>{recipientText(row)}</td>
                    <td className="ess-td"><span style={{ background: st.bg, color: st.text, padding: "3px 9px", borderRadius: 6, fontSize: 11, fontWeight: 600 }}>{st.label}</span></td>
                    <td className="ess-td" style={{ whiteSpace: "nowrap", textAlign: "right" }}>
                      <div style={{ display: "flex", gap: 4, justifyContent: "flex-end" }}>
                        <button title={inactive ? "Activate" : "Deactivate"} onClick={() => onToggleActive(row)} style={iconBtn(inactive ? "#225f2b" : "#0f766e")}><Check size={13} /></button>
                        <button title="Edit" onClick={() => openEdit(row)} style={iconBtn("#225f2b")}><Pencil size={13} /></button>
                        <button title="Delete" onClick={() => { if (window.confirm(`Delete rule "${row.name}"?`)) onDelete(row); }} style={iconBtn("#be123c")}><Trash2 size={13} /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <Modal open={showModal} onClose={() => setShowModal(false)} title={editing ? `Edit rule — ${editing.name}` : "New automation rule"} size="lg">
        <div>
          <FormField label="Rule name" required>
            <input value={form.name ?? ""} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} placeholder="Welcome email on lead creation" style={inputStyle} />
          </FormField>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            <FormField label="Trigger event" required>
              <select value={form.trigger_type ?? ""} onChange={(e) => setForm((p) => ({ ...p, trigger_type: e.target.value }))} style={inputStyle}>
                {Object.entries(TRIGGER_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </FormField>
            <FormField label={(() => {
              const tpl = templates.find((t) => String(t.id) === String(form.template_id));
              if (!tpl) return "Template";
              if (tpl.channel_type === "whatsapp") return "WhatsApp template";
              if (tpl.channel_type === "sms") return "SMS template";
              return "Email template";
            })()} required>
              <select value={form.template_id ?? ""} onChange={(e) => setForm((p) => ({ ...p, template_id: e.target.value }))} style={inputStyle}>
                <option value="">Select a template…</option>
                {templates.map((t) => <option key={t.id} value={t.id}>{t.name} ({t.channel_type || "email"})</option>)}
              </select>
            </FormField>
          </div>
          {(() => {
            const tpl = templates.find((t) => String(t.id) === String(form.template_id));
            if (!tpl) return null;
            const isWA = tpl.channel_type === "whatsapp";
            return (
              <div style={{ border: "1px solid #e2e7e2", borderRadius: 8, padding: 12, marginBottom: 14, background: "#fafbfa" }}>
                <div style={{ fontSize: 12, color: "#64748b", marginBottom: 6 }}>Template preview</div>
                {!isWA && tpl.subject && (
                  <div style={{ fontSize: 13, color: "#334155", marginBottom: 8 }}>
                    <strong>Subject:</strong> {tpl.subject}
                  </div>
                )}
                <div title="Preview — variables are resolved per lead on send" style={{ fontSize: 12.5, color: "#475569", lineHeight: 1.6, maxHeight: 180, overflowY: "auto", whiteSpace: isWA ? "pre-wrap" : "normal" }}>
                  {isWA
                    ? (tpl.body_text || <em style={{ color: "#94a3b8" }}>Empty body</em>)
                    : <span dangerouslySetInnerHTML={{ __html: tpl.body_html || "<em style='color:#94a3b8'>Empty body</em>" }} />}
                </div>
              </div>
            );
          })()}

          <FormField label="Recipients" required>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
              {(() => {
                const selectedTemplate = templates.find((t) => String(t.id) === String(form.template_id));
                const isWhatsApp = selectedTemplate?.channel_type === "whatsapp";
                const entries = isWhatsApp
                  ? Object.entries(RECIPIENT_LABELS).filter(([k]) => k.includes("_phone"))
                  : Object.entries(RECIPIENT_LABELS).filter(([k]) => !k.includes("_phone"));
                return entries.map(([k, v]) => (
                  <label key={k} style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 13, color: "#334155" }}>
                    <input type="checkbox" checked={form.recipients?.includes(k) || false} onChange={() => toggleRecipient(k)} style={{ width: 16, height: 16 }} />
                    {v}
                  </label>
                ));
              })()}
            </div>
          </FormField>
          {form.recipients?.includes("custom") && (
            <FormField label={(() => {
              const selectedTemplate = templates.find((t) => String(t.id) === String(form.template_id));
              return selectedTemplate?.channel_type === "whatsapp" ? "Custom phone (E.164)" : "Custom email address";
            })()} required>
              <input 
                value={(() => {
                  const selectedTemplate = templates.find((t) => String(t.id) === String(form.template_id));
                  return selectedTemplate?.channel_type === "whatsapp" ? (form.custom_phone || "") : (form.custom_email || "");
                })()} 
                onChange={(e) => {
                  const selectedTemplate = templates.find((t) => String(t.id) === String(form.template_id));
                  if (selectedTemplate?.channel_type === "whatsapp") {
                    setForm((p) => ({ ...p, custom_phone: e.target.value }));
                  } else {
                    setForm((p) => ({ ...p, custom_email: e.target.value }));
                  }
                }} 
                placeholder={(() => {
                  const selectedTemplate = templates.find((t) => String(t.id) === String(form.template_id));
                  return selectedTemplate?.channel_type === "whatsapp" ? "+237690000000" : "manager@example.com";
                })()} 
                style={inputStyle} 
              />
            </FormField>
          )}

          <div style={{ fontSize: 13, fontWeight: 700, color: "#334155", margin: "18px 0 10px" }}>Optional filters</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            <FormField label="To stage">
              <select value={form.cond_to_stage_id ?? ""} onChange={(e) => setForm((p) => ({ ...p, cond_to_stage_id: e.target.value }))} style={inputStyle}>
                <option value="">Any stage</option>
                {stages.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </FormField>
            <FormField label="From stage">
              <select value={form.cond_from_stage_id ?? ""} onChange={(e) => setForm((p) => ({ ...p, cond_from_stage_id: e.target.value }))} style={inputStyle}>
                <option value="">Any stage</option>
                {stages.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </FormField>
            <FormField label="Team">
              <select value={form.cond_team_id ?? ""} onChange={(e) => setForm((p) => ({ ...p, cond_team_id: e.target.value }))} style={inputStyle}>
                <option value="">Any team</option>
                {teams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </FormField>
            <FormField label="Salesperson">
              <select value={form.cond_user_id ?? ""} onChange={(e) => setForm((p) => ({ ...p, cond_user_id: e.target.value }))} style={inputStyle}>
                <option value="">Anyone</option>
                {users.map((u) => <option key={u.id} value={u.id}>{u.employee_name || u.login || u.email}</option>)}
              </select>
            </FormField>
            <FormField label="Priority">
              <select value={form.cond_priority ?? ""} onChange={(e) => setForm((p) => ({ ...p, cond_priority: e.target.value }))} style={inputStyle}>
                <option value="">Any priority</option>
                <option value="0">0 — Low</option>
                <option value="1">1 — Normal</option>
                <option value="2">2 — High</option>
                <option value="3">3 — Very high</option>
              </select>
            </FormField>
            <FormField label="Lost reason">
              <select value={form.cond_lost_reason_id ?? ""} onChange={(e) => setForm((p) => ({ ...p, cond_lost_reason_id: e.target.value }))} style={inputStyle}>
                <option value="">Any reason</option>
                {lostReasons.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            </FormField>
          </div>

          {form.trigger_type === "on_update" && (
            <FormField label="Notify only when these fields change">
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                {fieldOptions.map((o) => (
                  <label key={o.value} style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 13, color: "#334155" }}>
                    <input type="checkbox" checked={form.changed_fields?.includes(o.value) || false} onChange={() => toggleChangedField(o.value)} style={{ width: 16, height: 16 }} />
                    {o.label}
                  </label>
                ))}
              </div>
              <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 6 }}>
                Leave empty to notify on any business field change.
              </div>
            </FormField>
          )}

          <label style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6, cursor: "pointer" }}>
            <input type="checkbox" checked={form.is_active !== false} onChange={(e) => setForm((p) => ({ ...p, is_active: e.target.checked }))} style={{ width: 18, height: 18 }} />
            <span style={{ fontSize: 13, fontWeight: 600, color: "#334155" }}>Active</span>
          </label>

          <div style={{ display: "flex", gap: 12, justifyContent: "flex-end", borderTop: "1px solid #e2e7e2", paddingTop: 20, marginTop: 16 }}>
            <button onClick={() => setShowModal(false)} className="ess-btn ess-btn-secondary">Cancel</button>
            <button disabled={saving} onClick={submit} className="ess-btn ess-btn-primary">{saving ? "Saving…" : "Save"}</button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

const OUTBOX_BADGES = {
  outgoing: { text: "#64748b", bg: "#f1f5f9", label: "Outgoing" },
  sending: { text: "#b45309", bg: "#fffbeb", label: "Sending" },
  sent: { text: "#225f2b", bg: "#f0f7f0", label: "Sent" },
  failed: { text: "#be123c", bg: "#fff1f2", label: "Failed" },
};

const OutboxPanel = () => {
  const toast = useToast();
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState([]);
  const [stateFilter, setStateFilter] = useState("");
  const [sourceFilter, setSourceFilter] = useState("");
  const [channelFilter, setChannelFilter] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (silent) => {
    if (!silent) setLoading(true);
    try {
      const d = await api.mail.outbox.list({ limit: 100, state: stateFilter || undefined, source: sourceFilter || undefined, channel_type: channelFilter || undefined });
      setRows(d.rows || []);
      setStats(d.stats || []);
    } catch (e) {
      if (!silent) toast(e.message || "Failed to load mail log", "error");
    }
    setLoading(false);
  }, [stateFilter, sourceFilter, channelFilter, toast]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setInterval(() => load(true), 15000);
    return () => clearInterval(t);
  }, [load]);

  const retry = async (row) => {
    try {
      await api.mail.outbox.retry(row.lewoo_uid);
      toast("Queued for retry", "success");
      load(true);
    } catch (e) { toast(e.message || "Retry failed", "error"); }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete message to "${row.to_email || row.to_phone}"?`)) return;
    try {
      await api.mail.outbox.remove(row.lewoo_uid);
      toast("Deleted", "success");
      load(true);
    } catch (e) { toast(e.message || "Delete failed", "error"); }
  };

  const statCount = (state) => stats.find((s) => s.state === state)?.n || 0;

  const channelBadge = (ch) => {
    if (ch === "whatsapp") return { label: "WhatsApp", bg: "#f0fdf4", color: "#166534" };
    if (ch === "sms") return { label: "SMS", bg: "#f8fafc", color: "#475569" };
    return { label: "Email", bg: "#eff6ff", color: "#1e40af" };
  };

  const stateFilters = [
    { key: "", label: "All" },
    ...Object.entries(OUTBOX_BADGES).map(([key, b]) => ({ key, label: b.label })),
  ];

  const sourceFilters = [
    { key: "", label: "All sources" },
    { key: "rule", label: "Auto" },
    { key: "bulk", label: "Campaign" },
    { key: "sales_order", label: "Quote" },
  ];

  const channelFilters = [
    { key: "", label: "All channels" },
    { key: "email", label: "Email" },
    { key: "whatsapp", label: "WhatsApp" },
    { key: "sms", label: "SMS" },
  ];

  return (
    <div style={{ background: "white", borderRadius: 8, border: "1px solid #e2e7e2", overflow: "hidden" }}>
      <div style={{ display: "flex", gap: 12, alignItems: "center", padding: 16, borderBottom: "1px solid #e2e7e2", flexWrap: "wrap" }}>
        <div style={{ fontSize: 13, color: "#64748b" }}>
          Messages queued by automation rules and bulk campaigns — sent asynchronously. Auto-refreshes.
        </div>
        <div style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
          {["outgoing", "sending", "sent", "failed"].map((s) => {
            const b = OUTBOX_BADGES[s];
            return <span key={s} title={`${b.label}: ${statCount(s)}`} style={{ background: b.bg, color: b.text, padding: "3px 9px", borderRadius: 6, fontSize: 11, fontWeight: 700 }}>{b.label} {statCount(s)}</span>;
          })}
        </div>
      </div>
      <div style={{ display: "flex", gap: 6, padding: "12px 16px", borderBottom: "1px solid #e2e7e2", flexWrap: "wrap" }}>
        {stateFilters.map((f) => (
          <button
            key={f.key}
            onClick={() => setStateFilter(f.key)}
            style={{
              padding: "5px 12px", fontSize: 12, fontWeight: 600, borderRadius: 6, cursor: "pointer",
              border: "1px solid " + (stateFilter === f.key ? "#225f2b" : "#cbd5e1"),
              background: stateFilter === f.key ? "#225f2b" : "white",
              color: stateFilter === f.key ? "white" : "#475569",
            }}
          >
            {f.label}
          </button>
        ))}
        <div style={{ width: 1, background: "#e2e7e2", margin: "0 6px" }} />
        {sourceFilters.map((f) => (
          <button
            key={f.key}
            onClick={() => setSourceFilter(f.key)}
            style={{
              padding: "5px 12px", fontSize: 12, fontWeight: 600, borderRadius: 6, cursor: "pointer",
              border: "1px solid " + (sourceFilter === f.key ? "#0f766e" : "#cbd5e1"),
              background: sourceFilter === f.key ? "#0f766e" : "white",
              color: sourceFilter === f.key ? "white" : "#475569",
            }}
          >
            {f.label}
          </button>
        ))}
        <div style={{ width: 1, background: "#e2e7e2", margin: "0 6px" }} />
        {channelFilters.map((f) => (
          <button
            key={f.key}
            onClick={() => setChannelFilter(f.key)}
            style={{
              padding: "5px 12px", fontSize: 12, fontWeight: 600, borderRadius: 6, cursor: "pointer",
              border: "1px solid " + (channelFilter === f.key ? "#b45309" : "#cbd5e1"),
              background: channelFilter === f.key ? "#b45309" : "white",
              color: channelFilter === f.key ? "white" : "#475569",
            }}
          >
            {f.label}
          </button>
        ))}
      </div>
      {loading ? (
        <LoadingScreen />
      ) : rows.length === 0 ? (
        <EmptyState  title="No messages" subtitle="Triggered notifications will appear here" />
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "#fafbfa", borderBottom: "1px solid #e2e7e2" }}>
                <th className="ess-th" style={{ minWidth: 200 }}>To</th>
                <th className="ess-th" style={{ minWidth: 260 }}>Subject / Body</th>
                <th className="ess-th" style={{ minWidth: 100 }}>Channel</th>
                <th className="ess-th" style={{ minWidth: 150 }}>Lead</th>
                <th className="ess-th" style={{ minWidth: 140 }}>Rule</th>
                <th className="ess-th" style={{ minWidth: 100 }}>Source</th>
                <th className="ess-th" style={{ minWidth: 100 }}>State</th>
                <th className="ess-th" style={{ minWidth: 80, textAlign: "right" }}>Tries</th>
                <th className="ess-th" style={{ minWidth: 150 }}>Sent at</th>
                <th className="ess-th ess-th-r" style={{ minWidth: 110 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => {
                const b = OUTBOX_BADGES[row.state] || OUTBOX_BADGES.outgoing;
                const src = row.source === "bulk"
                  ? { label: "Campaign", bg: "#eff6ff", color: "#1e40af" }
                  : row.source === "sales_order"
                    ? { label: "Quote", bg: "#fef3c7", color: "#92400e" }
                    : { label: "Auto", bg: "#f1f5f9", color: "#64748b" };
                const ch = channelBadge(row.channel_type);
                return (
                  <tr key={row.lewoo_uid} className="ess-tr" style={{ borderBottom: i < rows.length - 1 ? "1px solid #f1f3f0" : "none" }}>
                    <td className="ess-td" style={{ color: "#1e293b", whiteSpace: "nowrap" }}>{row.to_email || row.to_phone || "—"}</td>
                    <td className="ess-td" style={{ maxWidth: 320 }}>
                      <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: "#334155" }}>{row.subject || (row.body || "").slice(0, 80) || "—"}</div>
                      {row.error && <div title={row.error} style={{ fontSize: 11, color: "#be123c", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 320 }}>{row.error}</div>}
                    </td>
                    <td className="ess-td"><span style={{ background: ch.bg, color: ch.color, padding: "3px 9px", borderRadius: 6, fontSize: 11, fontWeight: 600 }}>{ch.label}</span></td>
                    <td className="ess-td" style={{ color: "#64748b", whiteSpace: "nowrap" }}>{row.lead_name || "—"}</td>
                    <td className="ess-td" style={{ color: "#64748b", whiteSpace: "nowrap" }}>{row.rule_name || "—"}</td>
                    <td className="ess-td"><span style={{ background: src.bg, color: src.color, padding: "3px 9px", borderRadius: 6, fontSize: 11, fontWeight: 600 }}>{src.label}</span></td>
                    <td className="ess-td"><span style={{ background: b.bg, color: b.text, padding: "3px 9px", borderRadius: 6, fontSize: 11, fontWeight: 600 }}>{b.label}</span></td>
                    <td className="ess-td" style={{ textAlign: "right", color: "#475569" }}>{row.attempts ?? 0}</td>
                    <td className="ess-td" style={{ color: "#64748b", whiteSpace: "nowrap" }}>{fmt.dateTime(row.sent_at)}</td>
                    <td className="ess-td" style={{ whiteSpace: "nowrap", textAlign: "right" }}>
                      <div style={{ display: "flex", gap: 4, justifyContent: "flex-end" }}>
                        {row.state !== "sent" && row.state !== "sending" && (
                          <button title="Retry now" onClick={() => retry(row)} style={iconBtn("#0f766e")}><RefreshCw size={13} /></button>
                        )}
                        <button title="Delete" onClick={() => remove(row)} style={iconBtn("#be123c")}><Trash2 size={13} /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
const WhatsappPanel = ({ devices, stats, health, onRefresh }) => {
  const toast = useToast();
  const [showAdd, setShowAdd] = useState(false);
  const [showTest, setShowTest] = useState(false);
  const [showQr, setShowQr] = useState(null);
  const [qrCode, setQrCode] = useState("");
  const [qrImgBroken, setQrImgBroken] = useState(false);
  const [qrLoading, setQrLoading] = useState(false);
  const [qrError, setQrError] = useState("");
  const [qrStatusNote, setQrStatusNote] = useState("");
  const qrOpenedAt = useRef(0);
  const qrConfirm = useRef(0);

  const closeQr = () => {
    setShowQr(null);
    setQrCode("");
    setQrError("");
    setQrImgBroken(false);
    setQrLoading(false);
    setQrStatusNote("");
    qrConfirm.current = 0;
  };

  // Comparaison stricte : on refuse les strings genre "false"/"connecting" (truthy mais pas connecté)
  const isStrictConnected = (v) =>
    v === true || v === 1 || v === "true" || v === "1" || v === "connected" || v === "open";
  const [form, setForm] = useState({ device_name: "" });
  const [testForm, setTestForm] = useState({ to: "", body: "", device_id: "" });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [connecting, setConnecting] = useState({});

  const submitAdd = async () => {
    if (!form.device_name.trim()) { toast("Device name is required", "error"); return; }
    setSaving(true);
    try {
      await api.mail.whatsapp.devices.create({ device_name: form.device_name.trim() });
      toast("Device added. Connect it to scan QR code.", "success");
      setForm({ device_name: "" });
      setShowAdd(false);
      onRefresh();
    } catch (e) { toast(e.message || "Create failed", "error"); }
    setSaving(false);
  };

  // Convertit le payload backend en src affichable dans <img>.
  // Cas 1: data:image/... ou http(s):// -> affichable tel quel.
  // Cas 2: string brute Baileys ("2@...") -> on la rend via un renderer QR public.
  const toQrSrc = (raw) => {
    if (!raw || typeof raw !== "string") return "";
    const s = raw.trim();
    if (!s) return "";
    if (s.startsWith("data:image")) return s;
    if (/^https?:\/\//i.test(s)) return s;
    if (s.startsWith("data:")) return s;
    return `https://api.qrserver.com/v1/create-qr-code/?size=280x280&data=${encodeURIComponent(s)}`;
  };

  // Le backend V2 renvoie {qr (brut whatsapp-web.js), qr_code (dataURL), conversion_ok}.
  // On préfère toujours la dataURL ; à défaut on rend le brut via qrserver.
  const extractQrSrc = (qrRes) => {
    if (typeof qrRes === "string") return toQrSrc(qrRes.trim());
    if (!qrRes || typeof qrRes !== "object") return "";
    const cands = [
      qrRes.qr_code,
      qrRes.qr,
      qrRes.qrCode,
      qrRes.qrImage,
      qrRes.image,
      qrRes.data?.qr_code,
      qrRes.data?.qr,
      qrRes.data?.qrCode,
      qrRes.data?.data?.qr_code,
      qrRes.data?.data?.qr,
    ];
    const dataUrl = cands.find((s) => typeof s === "string" && s.trim().startsWith("data:image"));
    if (dataUrl) return dataUrl.trim();
    const raw = cands.find((s) => typeof s === "string" && s.trim()) || "";
    return toQrSrc(raw);
  };

  const fetchQrWithRetry = async (id, maxAttempts = 12) => {
    let lastErr = null;
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      try {
        const qrRes = await api.mail.whatsapp.devices.qr(id);
        const src = extractQrSrc(qrRes);
        if (src) return src;
      } catch (err) {
        const msg = String(err?.message || "");
        // 503 = échec d'init côté serveur (ex: Chrome manquant) -> inutile de poller 24s
        if (/503|chrome|initiali[sz]e|executable|launch/i.test(msg)) {
          throw new Error(`WhatsApp indisponible côté serveur : ${msg}`);
        }
        lastErr = err;
        // 404/408/425 = QR pas encore prêt côté backend -> on continue le polling
        const retryable = /404|408|425|pas encore|not ready|not found|no qr|empty|timeout/i.test(msg);
        if (!retryable && attempt >= 2) throw err;
      }
      await new Promise((r) => setTimeout(r, 2000));
    }
    if (lastErr) throw lastErr;
    return "";
  };

  const handleConnect = async (id) => {
    setConnecting((p) => ({ ...p, [id]: true }));
    setShowQr(id);
    qrOpenedAt.current = Date.now();
    qrConfirm.current = 0;
    setQrStatusNote("");
    setQrCode("");
    setQrImgBroken(false);
    setQrError("");
    setQrLoading(true);
    // 1) Demande de connexion (ne bloque pas le QR : le backend génère le QR en async)
    // Si le backend répond 409 "already connecting", on continue quand même vers le QR.
    try {
      await api.mail.whatsapp.devices.connect(id);
      toast("Connecting... Scan the QR code with WhatsApp.", "info");
    } catch (e) {
      const msg = String(e?.message || "");
      if (/already|en cours|connecting/i.test(msg)) {
        toast("Session déjà en cours de connexion — récupération du QR…", "info");
      } else {
        setQrError(e.message || "Connect failed");
        toast(e.message || "Connect failed", "error");
        setConnecting((p) => ({ ...p, [id]: false }));
        setQrLoading(false);
        return;
      }
    }
    // 2) Polling long du QR (le backend met souvent 5-15s à le générer)
    try {
      const src = await fetchQrWithRetry(id, 12);
      if (src) {
        setQrCode(src);
      } else {
        setQrError("QR code non reçu — cliquez sur Refresh QR");
        toast("QR code non reçu - réessayez Refresh QR", "error");
      }
      onRefresh();
    } catch (e) {
      setQrError(e.message || "QR non reçu");
      toast(e.message || "Connect failed", "error");
      setQrCode("");
    }
    setConnecting((p) => ({ ...p, [id]: false }));
    setQrLoading(false);
  };

  const handleRefreshQr = async (id) => {
    setQrLoading(true);
    setQrImgBroken(false);
    setQrError("");
    try {
      const src = await fetchQrWithRetry(id, 8);
      if (src) setQrCode(src);
      else {
        setQrError("QR toujours vide — attendez 10s puis réessayez");
        toast("QR toujours vide — réessayez dans 10s", "error");
      }
    } catch (e) {
      setQrError(e.message || "Refresh failed");
      toast(e.message || "Refresh failed", "error");
    }
    setQrLoading(false);
  };

  // Polling auto : tant que la modale QR est ouverte, vérifier si le device est passé en connected
  // Le backend met 5-10s après le scan pour passer is_connected=TRUE.
  // Anti-fermeture fantôme : on ne ferme que sur 2 lectures positives CONSÉCUTIVES
  // et au moins 8s après l'ouverture (le backend remonte souvent un état transitoire juste après connect).
  // Règle : Refresh = GET /status + GET /qr uniquement, jamais POST /connect (idempotent côté backend).
  useEffect(() => {
    if (!showQr) return;
    let cancelled = false;
    const readStatus = async () => {
      // -> {connected: bool strict, state: string, error: string}
      try {
        const s = await api.mail.whatsapp.devices.status(showQr);
        const st = s?.connected ?? s?.is_connected ?? s?.data?.connected ?? s?.data?.is_connected ?? false;
        const state = String(s?.state ?? s?.status ?? s?.data?.state ?? s?.data?.status ?? "").toLowerCase();
        if (isStrictConnected(st)) return { connected: true, state, error: "" };
        if (state === "error" || state === "failed") {
          const raw = s?.error ?? s?.data?.error ?? s?.data?.message ?? "";
          const msg = typeof raw === "string" && raw.trim() ? raw.trim() : "Erreur session WhatsApp côté serveur";
          return { connected: false, state, error: msg };
        }
        return { connected: false, state, error: "" };
      } catch {}
      try {
        const list = await api.mail.whatsapp.devices.list();
        const arr = Array.isArray(list) ? list : list?.data || [];
        const dev = arr.find((d) => String(d.id) === String(showQr));
        const st = dev?.is_connected ?? dev?.connected ?? false;
        if (isStrictConnected(st)) return { connected: true, state: "", error: "" };
      } catch {}
      return { connected: false, state: "", error: "" };
    };
    const poll = async () => {
      const r = await readStatus();
      if (cancelled) return;
      const elapsed = Date.now() - (qrOpenedAt.current || Date.now());
      if (r.error) {
        qrConfirm.current = 0;
        setQrError(r.error);
        setQrStatusNote(`État serveur : ${r.state} — corrigez puis cliquez Refresh QR`);
        return;
      }
      if (r.connected) {
        qrConfirm.current += 1;
        setQrStatusNote(`Signal connecté détecté — confirmation ${qrConfirm.current}/2…`);
        if (qrConfirm.current >= 2 && elapsed > 8000) {
          toast("Device connected !", "success");
          closeQr();
          onRefresh();
        }
      } else {
        qrConfirm.current = 0;
        const extra = r.state ? `État : ${r.state} — ` : "";
        setQrStatusNote(`${extra}En attente du scan… (vérifié à ${new Date().toLocaleTimeString()})`);
      }
    };
    poll();
    const id = setInterval(poll, 3000);
    // Le QR expire (~60s) : on le régénère silencieusement toutes les 50s tant que la modale est ouverte
    const qrTimer = setInterval(async () => {
      try {
        const qrRes = await api.mail.whatsapp.devices.qr(showQr);
        const src = extractQrSrc(qrRes);
        if (src && !cancelled) { setQrCode(src); setQrImgBroken(false); }
      } catch {}
    }, 50000);
    return () => { cancelled = true; clearInterval(id); clearInterval(qrTimer); };
  }, [showQr]);

  const handleDisconnect = async (id) => {
    try {
      await api.mail.whatsapp.devices.disconnect(id);
      toast("Disconnected", "success");
      onRefresh();
    } catch (e) { toast(e.message || "Disconnect failed", "error"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this device? This will remove the WhatsApp session.")) return;
    try {
      await api.mail.whatsapp.devices.remove(id);
      toast("Device deleted", "success");
      onRefresh();
    } catch (e) { toast(e.message || "Delete failed", "error"); }
  };

  const handleTest = async () => {
    if (!testForm.to.trim()) { toast("Recipient number is required", "error"); return; }
    setTesting(true);
    try {
      const body = { to: testForm.to.trim(), body: testForm.body.trim() || "Test message from LewooDB" };
      if (testForm.device_id) body.device_id = parseInt(testForm.device_id);
      await api.mail.whatsapp.test(body);
      toast("Test message queued", "success");
      setShowTest(false);
      setTestForm({ to: "", body: "", device_id: "" });
    } catch (e) { toast(e.message || "Test failed", "error"); }
    setTesting(false);
  };

  const connectedCount = devices.filter((d) => d.is_connected).length;
  const totalSent = stats?.total_sent_today || 0;
  const totalFailed = stats?.total_failed_today || 0;

  return (
    <div style={{ background: "white", borderRadius: 8, border: "1px solid #e2e7e2", overflow: "hidden" }}>
      <div style={{ display: "flex", gap: 12, alignItems: "center", padding: 16, borderBottom: "1px solid #e2e7e2", flexWrap: "wrap" }}>
        <div style={{ fontSize: 13, color: "#64748b" }}>
          WhatsApp devices connected via Baileys. Each device sends messages using its own number. Rate limited to avoid bans.
        </div>
        <div style={{ marginLeft: "auto", display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          {health && (
            <span style={{ fontSize: 12, fontWeight: 600, color: health.connected > 0 ? "#225f2b" : "#be123c", background: health.connected > 0 ? "#f0f7f0" : "#fff1f2", padding: "4px 10px", borderRadius: 6 }}>
              {health.connected}/{health.total_devices} connected
            </span>
          )}
          <span style={{ fontSize: 12, color: "#64748b" }}>{totalSent} sent today</span>
          {totalFailed > 0 && <span style={{ fontSize: 12, color: "#be123c" }}>{totalFailed} failed</span>}
          <button onClick={() => setShowAdd(true)} className="ess-btn ess-btn-primary"><Plus size={16} /> Add device</button>
          <button onClick={() => setShowTest(true)} className="ess-btn ess-btn-secondary"><Send size={16} /> Test</button>
        </div>
      </div>

      {devices.length === 0 ? (
        <div style={{ padding: 40, textAlign: "center" }}>
          <div style={{ fontSize: 13, color: "#64748b", marginBottom: 12 }}>No WhatsApp devices configured.</div>
          <button onClick={() => setShowAdd(true)} className="ess-btn ess-btn-primary">Add your first device</button>
        </div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "#fafbfa", borderBottom: "1px solid #e2e7e2" }}>
                <th className="ess-th" style={{ minWidth: 180 }}>Name</th>
                <th className="ess-th" style={{ minWidth: 160 }}>Phone</th>
                <th className="ess-th" style={{ minWidth: 120 }}>Status</th>
                <th className="ess-th" style={{ minWidth: 100 }}>Daily count</th>
                <th className="ess-th" style={{ minWidth: 140 }}>Last seen</th>
                <th className="ess-th ess-th-r" style={{ minWidth: 200 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {devices.map((d, i) => (
                <tr key={d.id} className="ess-tr" style={{ borderBottom: i < devices.length - 1 ? "1px solid #f1f3f0" : "none", opacity: d.is_active ? 1 : 0.6 }}>
                  <td className="ess-td" style={{ fontWeight: 600, color: "#225f2b" }}>{d.device_name}</td>
                  <td className="ess-td" style={{ whiteSpace: "nowrap" }}>{d.phone_number || "—"}</td>
                  <td className="ess-td">
                    <span style={{ background: d.is_connected ? "#f0f7f0" : "#fff1f2", color: d.is_connected ? "#225f2b" : "#be123c", padding: "3px 9px", borderRadius: 6, fontSize: 11, fontWeight: 600 }}>
                      {d.is_connected ? "Connected" : "Disconnected"}
                    </span>
                  </td>
                  <td className="ess-td" style={{ textAlign: "right" }}>
                    <span style={{ color: (d.daily_count || 0) >= 70 ? "#b45309" : "#475569" }}>{d.daily_count || 0} / 80</span>
                  </td>
                  <td className="ess-td" style={{ whiteSpace: "nowrap", color: "#64748b" }}>{d.last_seen ? fmt.dateTime(d.last_seen) : "—"}</td>
                  <td className="ess-td" style={{ whiteSpace: "nowrap", textAlign: "right" }}>
                    <div style={{ display: "flex", gap: 4, justifyContent: "flex-end" }}>
                      {!d.is_connected ? (
                        <button title="Connect" onClick={() => handleConnect(d.id)} disabled={connecting[d.id]} style={iconBtn("#225f2b")}>
                          {connecting[d.id] ? "Connecting…" : "Connect"}
                        </button>
                      ) : (
                        <button title="Disconnect" onClick={() => handleDisconnect(d.id)} style={iconBtn("#0f766e")}>Disconnect</button>
                      )}
                      <button title="Delete" onClick={() => handleDelete(d.id)} style={iconBtn("#be123c")}><Trash2 size={13} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal
        open={!!showQr}
        onClose={closeQr}
        title={`Connect WhatsApp — ${devices.find((d) => String(d.id) === String(showQr))?.device_name || ""}`}
        size="sm"
      >
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: "#334155", marginBottom: 10 }}>Scannez ce QR avec WhatsApp sur votre téléphone</div>
          {qrCode && !qrImgBroken ? (
            <>
              <img
                src={qrCode}
                alt="QR Code"
                onError={() => setQrImgBroken(true)}
                style={{ maxWidth: 280, height: "auto", border: "1px solid #e2e7e2", borderRadius: 8, background: "#fff", margin: "0 auto" }}
              />
              <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 8 }}>QR régénéré auto toutes les 50s (expire ~60s)</div>
            </>
          ) : qrImgBroken ? (
            <div style={{ padding: 20, color: "#be123c" }}>
              <div style={{ fontSize: 13, fontWeight: 600 }}>QR illisible (image cassée)</div>
              <div style={{ fontSize: 11, marginTop: 6 }}>Le backend a renvoyé un format non affichable — cliquez sur Refresh QR.</div>
            </div>
          ) : (
            <div style={{ padding: 20, color: "#64748b" }}>
              <div>{qrLoading ? "Génération du QR code… (5-15s)" : "En attente du QR code…"}</div>
              {qrError ? (
                <div style={{ fontSize: 11, marginTop: 6, color: "#be123c" }}>{qrError}</div>
              ) : (
                <div style={{ fontSize: 11, marginTop: 6 }}>Le backend génère le QR en async — récupération automatique en cours</div>
              )}
            </div>
          )}
          {qrStatusNote && (
            <div style={{ fontSize: 11, color: "#0f766e", marginTop: 8, fontWeight: 600 }}>{qrStatusNote}</div>
          )}
          <div style={{ marginTop: 12, display: "flex", gap: 8, justifyContent: "center" }}>
            <button onClick={closeQr} className="ess-btn ess-btn-secondary">Close</button>
            <button onClick={() => handleRefreshQr(showQr)} disabled={qrLoading} className="ess-btn ess-btn-primary">
              <RefreshCw size={14} /> {qrLoading ? "Refreshing…" : "Refresh QR"}
            </button>
          </div>
          <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 10 }}>
            Si le scan ne passe pas : vérifiez WhatsApp &gt; Appareils liés (max 4) et, après un redémarrage serveur, supprimez puis recréez le device si besoin.
          </div>
        </div>
      </Modal>

      {showAdd && (
        <div style={{ padding: 20, borderTop: "1px solid #e2e7e2", background: "#fafbfa" }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: "#334155", marginBottom: 10 }}>Add WhatsApp device</div>
          <div style={{ maxWidth: 400 }}>
            <FormField label="Device name" required>
              <input value={form.device_name} onChange={(e) => setForm((p) => ({ ...p, device_name: e.target.value }))} placeholder="e.g. Main number" style={inputStyle} />
            </FormField>
            <div style={{ fontSize: 11, color: "#64748b", marginBottom: 12 }}>
              The phone number will be detected automatically after scanning the QR code with WhatsApp.
            </div>
          </div>
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 14 }}>
            <button onClick={() => setShowAdd(false)} className="ess-btn ess-btn-secondary">Cancel</button>
            <button disabled={saving} onClick={submitAdd} className="ess-btn ess-btn-primary">{saving ? "Adding…" : "Add device"}</button>
          </div>
        </div>
      )}

      {showTest && (
        <div style={{ padding: 20, borderTop: "1px solid #e2e7e2", background: "#fafbfa" }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: "#334155", marginBottom: 10 }}>Send test message</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, maxWidth: 600 }}>
            <FormField label="To (E.164)" required>
              <input value={testForm.to} onChange={(e) => setTestForm((p) => ({ ...p, to: e.target.value }))} placeholder="+237690000000" style={inputStyle} />
            </FormField>
            <FormField label="Device (optional)">
              <select value={testForm.device_id} onChange={(e) => setTestForm((p) => ({ ...p, device_id: e.target.value }))} style={inputStyle}>
                <option value="">Auto (round-robin)</option>
                {devices.map((d) => <option key={d.id} value={d.id}>{d.device_name} ({d.phone_number})</option>)}
              </select>
            </FormField>
            <FormField label="Message" required>
              <textarea value={testForm.body} onChange={(e) => setTestForm((p) => ({ ...p, body: e.target.value }))} rows={2} placeholder="Hello from LewooDB!" style={{ ...inputStyle, resize: "vertical" }} />
            </FormField>
          </div>
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 14 }}>
            <button onClick={() => setShowTest(false)} className="ess-btn ess-btn-secondary">Cancel</button>
            <button disabled={testing} onClick={handleTest} className="ess-btn ess-btn-primary">{testing ? "Sending…" : "Send test"}</button>
          </div>
        </div>
      )}
    </div>
  );
};

const WhatsappInboxPanel = ({ onRefresh }) => {
  const toast = useToast();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (silent) => {
    if (!silent) setLoading(true);
    try {
      const d = api.mail.whatsapp && api.mail.whatsapp.inbox ? await api.mail.whatsapp.inbox.list({ limit: 100 }) : { data: [] };
      setRows(d?.data || []);
    } catch (e) {
      if (!silent) toast(e.message || "Failed to load inbox", "error");
    }
    setLoading(false);
  }, [toast]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setInterval(() => load(true), 15000);
    return () => clearInterval(t);
  }, [load]);

  const remove = async (id) => {
    if (!window.confirm("Delete this message?")) return;
    try {
      await api.mail.whatsapp.inbox.remove(id);
      toast("Deleted", "success");
      load(true);
      onRefresh?.();
    } catch (e) { toast(e.message || "Delete failed", "error"); }
  };

  if (loading) return <LoadingScreen />;

  return (
    <div style={{ background: "white", borderRadius: 8, border: "1px solid #e2e7e2", overflow: "hidden" }}>
      <div style={{ display: "flex", gap: 12, alignItems: "center", padding: 16, borderBottom: "1px solid #e2e7e2", flexWrap: "wrap" }}>
        <div style={{ fontSize: 13, color: "#64748b" }}>
          Incoming WhatsApp messages received by your devices. Auto-refreshes.
        </div>
        <div style={{ marginLeft: "auto" }}>
          <button onClick={() => load(false)} className="ess-btn ess-btn-secondary"><RefreshCw size={14} /> Refresh</button>
        </div>
      </div>
      {rows.length === 0 ? (
        <EmptyState title="No incoming messages" subtitle="Messages sent to your WhatsApp numbers will appear here" />
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "#fafbfa", borderBottom: "1px solid #e2e7e2" }}>
                <th className="ess-th" style={{ minWidth: 160 }}>From</th>
                <th className="ess-th" style={{ minWidth: 120 }}>To</th>
                <th className="ess-th" style={{ minWidth: 300 }}>Body</th>
                <th className="ess-th" style={{ minWidth: 100 }}>Type</th>
                <th className="ess-th" style={{ minWidth: 140 }}>Received at</th>
                <th className="ess-th" style={{ minWidth: 140 }}>Linked lead</th>
                <th className="ess-th ess-th-r" style={{ minWidth: 90 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.id} className="ess-tr" style={{ borderBottom: i < rows.length - 1 ? "1px solid #f1f3f0" : "none" }}>
                  <td className="ess-td" style={{ whiteSpace: "nowrap", color: "#1e293b" }}>{r.from_phone || "—"}</td>
                  <td className="ess-td" style={{ whiteSpace: "nowrap", color: "#64748b" }}>{r.to_phone || "—"}</td>
                  <td className="ess-td" style={{ maxWidth: 320, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: "#334155" }}>{r.body || "—"}</td>
                  <td className="ess-td" style={{ whiteSpace: "nowrap" }}>{r.message_type || "text"}</td>
                  <td className="ess-td" style={{ whiteSpace: "nowrap", color: "#64748b" }}>{r.received_at ? fmt.dateTime(r.received_at) : "—"}</td>
                  <td className="ess-td" style={{ whiteSpace: "nowrap", color: r.lead_id ? "#225f2b" : "#94a3b8" }}>{r.lead_id ? `Lead #${r.lead_id}` : "—"}</td>
                  <td className="ess-td" style={{ whiteSpace: "nowrap", textAlign: "right" }}>
                    <button title="Delete" onClick={() => remove(r.id)} style={iconBtn("#be123c")}><Trash2 size={13} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const AdminPanelCrmSettingsPage = () => {
  const toast = useToast();
  const [tab, setTab] = useState(() => {
    try { return localStorage.getItem("crmSettingsTab") || "stages"; } catch { return "stages"; }
  });
  const [loading, setLoading] = useState(true);

  const [stages, setStages] = useState([]);
  const [teams, setTeams] = useState([]);
  const [tags, setTags] = useState([]);
  const [lostReasons, setLostReasons] = useState([]);
  const [activityTypes, setActivityTypes] = useState([]);
  const [sources, setSources] = useState([]);
  const [mediums, setMediums] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [users, setUsers] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [rules, setRules] = useState([]);
  const [changedFieldsAllowed, setChangedFieldsAllowed] = useState([]);
  const [devices, setDevices] = useState([]);
  const [waStats, setWaStats] = useState(null);
  const [waHealth, setWaHealth] = useState(null);

  const loadAll = async () => {
    try {
      const waDevices = (api.mail.whatsapp && api.mail.whatsapp.devices && api.mail.whatsapp.devices.list ? api.mail.whatsapp.devices.list() : Promise.resolve([])).catch(() => []);
      const waStats = (api.mail.whatsapp && api.mail.whatsapp.stats ? api.mail.whatsapp.stats() : Promise.resolve(null)).catch(() => null);
      const waHealth = (api.mail.whatsapp && api.mail.whatsapp.health ? api.mail.whatsapp.health() : Promise.resolve(null)).catch(() => null);
      const [st, tm, tg, lr, at, src, med, camp, us, ml, rl, waDevsData, waStatsData, waHealthData] = await Promise.all([
        api.crm.stages.list({ limit: 500 }).catch(() => []),
        api.crm.teams.list({ limit: 500 }).catch(() => []),
        api.crm.tags.list({ limit: 500 }).catch(() => []),
        api.crm.lostReasons.list({ limit: 500 }).catch(() => []),
        api.crm.activityTypes.list({ limit: 500 }).catch(() => []),
        api.crm.sources.list({ limit: 500 }).catch(() => []),
        api.crm.mediums.list({ limit: 500 }).catch(() => []),
        api.crm.campaigns.list({ limit: 500 }).catch(() => []),
        api.users.list().catch(() => []),
        api.mail.templates.list({ limit: 500 }).catch(() => []),
        api.mail.rules.list({ limit: 500, include_inactive: true }).catch(() => ({ rows: [], changedFieldsAllowed: null })),
        waDevices,
        waStats,
        waHealth,
      ]);
      setStages(norm(st || []).slice().sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0)));
      setTeams(norm(tm || []));
      setTags(norm(tg || []));
      setLostReasons(norm(lr || []));
      setActivityTypes(norm(at || []));
      setSources(norm(src || []));
      setMediums(norm(med || []));
      setCampaigns(norm(camp || []));
      setUsers(norm(us || []));
      setTemplates(norm(ml || []));
      setRules(rl?.rows || []);
      setChangedFieldsAllowed(normalizeAllowedFields(rl?.changedFieldsAllowed));
      setDevices(norm(waDevsData || []));
      setWaStats(waStatsData?.data || null);
      setWaHealth(waHealthData?.data || null);
    } catch (e) {
      toast(e.message || "Failed to load settings", "error");
    }
    setLoading(false);
  };

  useEffect(() => { loadAll(); }, []);
  useEffect(() => {
    try { localStorage.setItem("crmSettingsTab", tab); } catch {}
  }, [tab]);

  const refresh = () => loadAll();

  const handleDelete = (apiObj) => async (row) => {
    try {
      await apiObj.remove(row.id);
      toast("Deleted", "success");
      refresh();
    } catch (e) {
      toast(e.message || "Delete failed", "error");
    }
  };

  const handleToggle = (apiObj) => async (row) => {
    try {
      await apiObj.update(row.id, { is_active: row.is_active === false });
      toast(row.is_active === false ? "Activated" : "Deactivated", "success");
      refresh();
    } catch (e) {
      toast(e.message || "Update failed", "error");
    }
  };

  const handleReorder = async (stage, dir) => {
    const idx = stages.findIndex((s) => s.id === stage.id);
    const swap = idx + dir;
    if (swap < 0 || swap >= stages.length) return;
    const next = stages.slice();
    [next[idx], next[swap]] = [next[swap], next[idx]];
    try {
      await api.crm.stages.reorder({ order: next.map((s) => s.id) });
      setStages(next);
      toast("Order updated", "success");
    } catch (e) {
      toast(e.message || "Reorder failed", "error");
    }
  };

  const TABS = [
    { key: "stages", label: "Stages" },
    { key: "teams", label: "Teams" },
    { key: "tags", label: "Tags" },
    { key: "lostReasons", label: "Lost reasons" },
    { key: "activityTypes", label: "Activity types" },
    { key: "sources", label: "Sources" },
    { key: "mediums", label: "Mediums" },
    { key: "campaigns", label: "Campaigns" },
    { key: "smtp", label: "Email SMTP" },
    { key: "whatsapp", label: "WhatsApp" },
    { key: "inbox", label: "Inbox" },
    { key: "templates", label: "Message templates" },
    { key: "rules", label: "Automation rules" },
    { key: "outbox", label: "Mail log" },
  ];

  return (
    <div className="max-w-7xl mx-auto animate-fade-in" style={{ padding: "0 16px" }}>
      <PageHeader title="CRM Settings" subtitle="Pipeline stages, teams, tags, marketing referentials and message templates" />

      <div style={{ display: "flex", gap: 4, borderBottom: "2px solid #e2e7e2", marginBottom: 24, overflowX: "auto" }}>
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            style={{
              padding: "9px 16px", fontSize: 13, fontWeight: 500, border: "none", cursor: "pointer",
              background: "transparent", whiteSpace: "nowrap",
              borderBottom: tab === t.key ? "2px solid #225f2b" : "2px solid transparent",
              color: tab === t.key ? "#225f2b" : "#64748b",
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading ? <LoadingScreen /> : (
        <>
          {tab === "stages" && (
            <StagePanel
              rows={stages}
              teams={teams}
              loading={false}
              onCreate={(p) => api.crm.stages.create(p).then(refresh)}
              onUpdate={(id, p) => api.crm.stages.update(id, p).then(refresh)}
              onDelete={handleDelete(api.crm.stages)}
              onToggleActive={handleToggle(api.crm.stages)}
              onReorder={handleReorder}
            />
          )}
          {tab === "teams" && (
            <TeamPanel
              rows={teams}
              users={users}
              loading={false}
              onCreate={(p) => api.crm.teams.create(p).then(refresh)}
              onUpdate={(id, p) => api.crm.teams.update(id, p).then(refresh)}
              onDelete={handleDelete(api.crm.teams)}
              onToggleActive={handleToggle(api.crm.teams)}
            />
          )}
          {tab === "tags" && (
            <TagPanel
              rows={tags}
              loading={false}
              onCreate={(p) => api.crm.tags.create(p).then(refresh)}
              onUpdate={(id, p) => api.crm.tags.update(id, p).then(refresh)}
              onDelete={handleDelete(api.crm.tags)}
              onToggleActive={handleToggle(api.crm.tags)}
            />
          )}
          {tab === "lostReasons" && (
            <LostReasonPanel
              rows={lostReasons}
              loading={false}
              onCreate={(p) => api.crm.lostReasons.create(p).then(refresh)}
              onUpdate={(id, p) => api.crm.lostReasons.update(id, p).then(refresh)}
              onDelete={handleDelete(api.crm.lostReasons)}
              onToggleActive={handleToggle(api.crm.lostReasons)}
            />
          )}
          {tab === "activityTypes" && (
            <ActivityTypePanel
              rows={activityTypes}
              loading={false}
              onCreate={(p) => api.crm.activityTypes.create(p).then(refresh)}
              onUpdate={(id, p) => api.crm.activityTypes.update(id, p).then(refresh)}
              onDelete={handleDelete(api.crm.activityTypes)}
              onToggleActive={handleToggle(api.crm.activityTypes)}
            />
          )}
          {tab === "sources" && (
            <SimplePanel
              rows={sources}
              title="Marketing sources used to track lead origin."
              
              emptyTitle="No sources"
              emptySub="Create your first source"
              nameField="Source"
              loading={false}
              onCreate={(p) => api.crm.sources.create(p).then(refresh)}
              onUpdate={(id, p) => api.crm.sources.update(id, p).then(refresh)}
              onDelete={handleDelete(api.crm.sources)}
              onToggleActive={handleToggle(api.crm.sources)}
            />
          )}
          {tab === "mediums" && (
            <SimplePanel
              rows={mediums}
              title="Marketing mediums (e.g. email, social, paid ads)."
              
              emptyTitle="No mediums"
              emptySub="Create your first medium"
              nameField="Medium"
              loading={false}
              onCreate={(p) => api.crm.mediums.create(p).then(refresh)}
              onUpdate={(id, p) => api.crm.mediums.update(id, p).then(refresh)}
              onDelete={handleDelete(api.crm.mediums)}
              onToggleActive={handleToggle(api.crm.mediums)}
            />
          )}
          {tab === "campaigns" && (
            <SimplePanel
              rows={campaigns}
              title="Marketing campaigns to attribute leads."
              
              emptyTitle="No campaigns"
              emptySub="Create your first campaign"
              nameField="Campaign"
              loading={false}
              onCreate={(p) => api.crm.campaigns.create(p).then(refresh)}
              onUpdate={(id, p) => api.crm.campaigns.update(id, p).then(refresh)}
              onDelete={handleDelete(api.crm.campaigns)}
              onToggleActive={handleToggle(api.crm.campaigns)}
            />
          )}
          {tab === "smtp" && <SmtpPanel />}
          {tab === "templates" && (
            <TemplatePanel
              rows={templates}
              loading={false}
              onCreate={(p) => api.mail.templates.create(p).then(refresh)}
              onUpdate={(id, p) => api.mail.templates.update(id, p).then(refresh)}
              onDelete={handleDelete(api.mail.templates)}
              onToggleActive={handleToggle(api.mail.templates)}
            />
          )}
          {tab === "rules" && (
            <RulesPanel
              rows={rules}
              templates={templates}
              stages={stages}
              teams={teams}
              users={users}
              lostReasons={lostReasons}
              changedFieldsAllowed={changedFieldsAllowed}
              loading={false}
              onCreate={(p) => api.mail.rules.create(p).then(refresh)}
              onUpdate={(id, p) => api.mail.rules.update(id, p).then(refresh)}
              onDelete={handleDelete(api.mail.rules)}
              onToggleActive={handleToggle(api.mail.rules)}
            />
          )}
          {tab === "outbox" && <OutboxPanel />}
          {tab === "whatsapp" && (
            <WhatsappPanel
              devices={devices}
              stats={waStats}
              health={waHealth}
              onRefresh={loadAll}
            />
          )}
          {tab === "inbox" && <WhatsappInboxPanel onRefresh={loadAll} />}
        </>
      )}
    </div>
  );
};

export default AdminPanelCrmSettingsPage;